/**
 * `vg lsp` — the Vibgrate language server.
 *
 * ONE ENGINE, MANY THIN CLIENTS. This process is the only thing that knows how
 * to score drift. Every editor client (Vibgrate for VS Code, Vibgrate for
 * JetBrains) is a renderer over the messages defined here. If you find yourself
 * adding scoring, banding, or thresholding logic to a client, it belongs here
 * instead — see `docs/IDE-INTEGRATION-PLAN.md` §3, §4.
 *
 * Two rules this file exists to enforce:
 *
 *  1. **The wire carries a `band`, never a colour.** Clients map band → their
 *     own native theme colour. Ship a hex from here and every client inherits
 *     the light-mode bug (plan §5.0).
 *  2. **Never emit an Error-severity diagnostic.** Vibgrate is not a gate.
 *     The engine's findings use `error` for EOL runtimes; we deliberately clamp
 *     that to Warning on the way out (plan §8.2). Erroring in the Problems panel
 *     is how an ambient tool gets uninstalled.
 */

import * as path from 'node:path';
import * as fs from 'node:fs';
import { pathToFileURL, fileURLToPath } from 'node:url';
import * as semver from 'semver';

import { runCoreScan, refineArchitectureWithGraph, refineArchitectureWithAstRoles } from '../core-open/index.js';
import type {
  DependencyRow,
  ProjectScan,
  ScanArtifact,
  ScanOptions,
  SurfaceInventory,
  ToolingInventoryResult,
} from '../core-open/index.js';

/** Element type of `ScanArtifact.findings` — avoids a deep-path type import. */
type Finding = NonNullable<ScanArtifact['findings']>[number];
import { perDependencyDrift } from '../core-open/scoring/dependency-drift-v3.js';
import { loadAdvancedScanHook } from '../reporting/advanced-hook.js';
import { VERSION } from '../version.js';
import { Connection } from './protocol.js';
import {
  findPackageLine,
  findRuntimeLine,
  endOfLine,
  isManifest,
  manifestKind,
} from './manifest-positions.js';
import { projectForFile as matchProjectForFile } from './project-for-file.js';
import { buildGraph } from '../engine/build.js';
import { fileRolesFromParseCache } from '../engine/ast-roles.js';
import type { AstRoleHit } from '../core-open/scanners/architecture/ast-roles.js';
import { boundaryProfileRules } from '../core-open/scanners/architecture/graph-refine.js';
import { loadGraph } from '../engine/load.js';
import {
  architectureSidecarSummary,
  loadArchitectureCallables,
  type ArchitectureCallableWire,
} from '../engine/haile/architecture-callables.js';
import { haileModuleStatus } from '../install/haile-module.js';
import { readHaileSidecar } from '../engine/haile/sidecar.js';
import { loadHaileProvider } from '../engine/haile/haile-provider.js';
import { overviewOf, sliceOf } from '../engine/chart/server.js';
import { architecturePageHtml } from '../engine/chart/page.js';
import { readArchUiScript } from '../engine/chart/arch-ui-script.js';
import {
  BOARD_LAYOUT_MAGIC,
  defaultBoardLayout,
  readBoardLayout,
  writeBoardLayout,
  type ArchBoardLayout,
} from '../engine/chart/board-layout.js';
import { showJsonFor } from '../engine/chart/model.js';
import { parseArchView, type ArchOverview, type ArchSlice } from '../engine/chart/arch-types.js';
import { writeArtifacts, resolveGraphPath } from '../engine/artifacts.js';
import { writeSnapshot } from '../engine/freshness.js';
import { loadGraphPreferIndex } from '../engine/index-db.js';
import type { RefreshOutcome } from '../engine/refresh.js';
import { RefreshScheduler } from '../engine/refresh-scheduler.js';
import { DaemonSemanticSession } from '../runtime/vgd/semantic-client.js';
import { envForNamedVgdSocket } from '../runtime/vgd/attach.js';
import { manifestHash, loadScanCache, writeScanCache, isDependencyFile } from './scan-cache.js';
import { isSkippedDirName } from '../engine/discover.js';
import { recordScore, lastEntry, deltaFrom, recentHistory, type ScoreHistoryEntry } from './score-history.js';
import { readIgnores, type IgnoreEntry } from './ignores.js';
import { readInventory, recordInventory, newlyDrifted, inventoryKey } from './inventory-history.js';
import { runGraphQuery, type GraphQueryParams, type GraphQueryResult } from './graph-query.js';
import {
  enrichVulns,
  scanWorkspaceVulns,
  type EnrichResult,
  type Reachability,
  type VulnTarget,
  type VulnsWorkspaceResult,
  type VulnReachSite,
} from './enrich.js';
import type { VgGraph } from '../schema.js';

// ── Wire types (the contract every client renders) ─────────────────────────

/** DriftScore band. Mirrors the engine — clients must never re-derive it. */
export type Band = 'low' | 'moderate' | 'high';

/**
 * `vibgrate/scanArtifact` — full Drift scan artifact as prettified JSON for
 * Output ▸ Vibgrate Scan. Kept off the main log channel so operational noise
 * (graph, engine, install) stays readable. Clients must not parse this for UI
 * state — score, band, and inventory still come from `vibgrate/score`.
 */
export interface ScanArtifactNotification {
  /** Pretty-printed scan artifact JSON (same shape as `vg scan --format json`). */
  json: string;
  rootPath: string;
  scannedAt: string;
  /** True when the artifact was replayed from the local LSP scan cache. */
  fromCache?: boolean;
}

/** `vibgrate/score` — pushed whenever the score changes. Drives the status bar. */
export interface ScoreNotification {
  score: number;
  /** DRIFTSCORE-V3-SPEC §5. Clients map band → theme colour; we never send one. */
  band: Band;
  /** `estimated` renders with a leading `~` (v3 §2.4). Offline is NOT estimated. */
  mode: 'verified' | 'estimated';
  /** Clients must break trend lines across a change here (v3 version-tag note). */
  methodology: string;
  scale: '0 best, 100 worst';
  counts: { behind: number; eol: number; unmaintained: number; total: number };
  rootPath: string;
  scannedAt: string;
  /**
   * Drift diff (plan §5.8): signed change from the previously recorded score,
   * engine-computed, same-methodology only. Absent — never 0 — when there is
   * no comparable previous score. Clients render `⌁ +4` / `⌁ -3`; they never
   * subtract two scores themselves.
   */
  delta?: number;
  /**
   * Full dependency inventory for the Dependencies panel — every scoreable
   * package in scope, ranked worst-first (current packages included at drift 0).
   * Built with the same per-dependency scorer as the DriftScore aggregate so the
   * panel number and the row numbers never disagree. Absent when there are no
   * scoreable dependencies. Vulnerability enrichment is fetched lazily on
   * expand, not carried here.
   */
  breakdown?: BreakdownItem[];
  /**
   * Upcoming/overdue runtime end-of-life dates (plan §5.6 "EOL horizon"),
   * soonest/most-overdue first. Built only from real endoflife.date cycles
   * already resolved onto each project (`ProjectScan.runtimeEolDate`) — never
   * the major-version-lag proxy, and never a client-side estimate. Absent when
   * no project has a known EOL date (e.g. an unmapped runtime, or offline).
   */
  eolHorizon?: EolHorizonItem[];
  /**
   * Scanned projects in this workspace (monorepo inventory). Clients use this
   * for a Snyk-style project picker: whole workspace vs one package path.
   * Absent or empty when the scan found nothing scoreable.
   */
  projects?: ProjectRef[];
}

/** One scanned package/project for the monorepo scope picker. */
export interface ProjectRef {
  /** Workspace-relative project path (`.` for the repo root package). */
  path: string;
  /** Human label — package name when known, else the path basename. */
  name: string;
  /** Per-project DriftScore when the engine scored this project. */
  score?: number;
  band?: Band;
  /** `estimated` → badge/tooltip use a leading `~` (same as the status bar). */
  mode?: 'verified' | 'estimated';
  /** Workspace-relative path to this project's manifest (package.json, .csproj, …). */
  manifestPath?: string;
  /** Workspace-relative path to this project's lockfile, when one was resolved. */
  lockfilePath?: string;
}

/**
 * `vibgrate/architecture` — pulled by the Architecture panel on scope change.
 * `undefined path` (or `'__repo__'`) returns the whole-workspace aggregate;
 * a project path returns that project's own architecture detection.
 */
export interface ArchitectureCoverageWire {
  classified: number;
  unclassified: number;
  ratio: number;
  bySource: Record<string, number>;
}

export interface ArchitectureUnclassifiedFolderWire {
  path: string;
  count: number;
  sampleFiles: string[];
}

export interface FolderClassificationWire {
  path: string;
  layer: string;
  confidence: number;
  fileCount: number;
  signals: string[];
}

export interface ArchitectureResponse {
  archetype: ProjectArchetypeWire;
  archetypeConfidence: number;
  layers: LayerSummaryWire[];
  totalClassified: number;
  unclassified: number;
  folders?: FolderClassificationWire[];
  unclassifiedFiles?: string[];
  coverage?: ArchitectureCoverageWire;
  unclassifiedFolders?: ArchitectureUnclassifiedFolderWire[];
  unclassifiedFoldersCapped?: true;
  classified?: Array<{
    filePath: string;
    layer: string;
    confidence: number;
    signals: string[];
    role?: 'controller' | 'service' | 'repository' | 'entity' | 'handler' | 'router';
  }>;
  workspaceShape?: 'single' | 'monorepo' | 'polyglot-platform';
  supportLevel?: 0 | 1 | 2 | 3 | 4;
  packIds?: string[];
  projectKind?: string;
  artifactKind?: string;
  contractKind?: string;
  semanticRole?: string;
  evidence?: ArchitectureEvidenceWire[];
  evidenceCapped?: true;
  workspaceEdges?: ArchitectureWorkspaceEdgeWire[];
  workspaceEdgesCapped?: true;
  violations?: ArchitectureViolationWire[];
  violationsCapped?: true;
  boundaryProfile?: string;
  /**
   * What `boundaryProfile` actually enforces, in prose, straight from the
   * checker. Present only when a profile is in force. Clients render this —
   * they must never author their own description of the rules.
   */
  boundaryRules?: string[];
  structure?: ArchitectureStructureWire;
  generated?: true;
  generatedBy?: string;
  sourceContract?: string;
  /** Workspace-relative manifest path — absent at whole-workspace scope. */
  manifestPath?: string;
  /** Workspace-relative lockfile path — absent at whole-workspace scope or when none was resolved. */
  lockfilePath?: string;
  /**
   * What the callables in this scope do, from the architecture module's
   * classify file (`vg build`). Omitted — not empty — when the module did not
   * run, the sidecar is stale for this graph, or nothing in scope classified.
   * Abstain / unknown rows are never sent. File-layer stays the interlingua:
   * `layer` on a row is a copy, never a relabel.
   */
  callables?: ArchitectureCallableWire[];
  /**
   * Where the architecture module stands, so a client can tell "nothing to
   * show" from "the engine tried to install it and could not" without
   * reading the module cache itself. Only `unavailable` justifies an offer.
   */
  architectureModule?: ArchitectureModuleWire;
  /**
   * Workspace map (packages + aggregate edges). Omitted when the code graph
   * is not loaded. Clients paint this, never the raw  graph.
   */
  overview?: ArchOverview;
  /**
   * Column slice for a project scope. Omitted at whole-workspace scope or
   * when the path does not match a package.
   */
  slice?: ArchSlice;
  /**
   * External Surfaces — the tech stack and the third-party surfaces this code
   * talks to, for the Architecture view's Externals tab. A pure projection of
   * `extended.toolingInventory` and `extended.surfaceInventory`; omitted when
   * the scan produced neither, so a client can tell "not scanned" from
   * "none found".
   */
  externals?: ArchitectureExternalsWire;
}

/** One labelled row of the tech stack, e.g. "Frontend" -> React 19.3.0. */
export interface TechStackGroupWire {
  label: string;
  items: Array<{ name: string; version: string | null }>;
}

export interface ArchitectureExternalsWire {
  techStack?: TechStackGroupWire[];
  /**
   * The surface inventory verbatim from the artifact. Category, icon id and
   * freshness are the catalog's verdicts — the client renders them and never
   * re-derives one, exactly as it never re-derives a drift band.
   */
  surfaces?: SurfaceInventory;
}

export interface ArchitectureModuleWire {
  status: 'present' | 'unavailable' | 'absent' | 'disabled' | 'declined';
  version?: string;
  engineVersion?: string;
  symbolCount?: number;
}

type ProjectArchetypeWire = string;

interface ArchitectureEvidenceWire {
  packId: string;
  dimension: string;
  value: string;
  confidence: number;
  signals: string[];
}

interface ArchitectureWorkspaceEdgeWire {
  kind: 'implements-contract' | 'generates' | 'deploys' | 'provisions' | 'depends_on';
  fromProject: string;
  toProject: string;
  fromFile?: string;
  toFile?: string;
  confidence: number;
}

interface ArchitectureViolationWire {
  fromFile: string;
  toFile: string;
  fromLayer: string;
  toLayer: string;
  edgeKind: string;
  rule: string;
  confidence: number;
}

interface ArchitectureStructureWire {
  primary: string;
  characteristics: string[];
  confidence: number;
}

function architectureWire(arch: {
  archetype: string;
  archetypeConfidence: number;
  layers: LayerSummaryWire[];
  totalClassified: number;
  unclassified: number;
  folders?: FolderClassificationWire[];
  unclassifiedFiles?: string[];
  coverage?: ArchitectureCoverageWire;
  unclassifiedFolders?: ArchitectureUnclassifiedFolderWire[];
  unclassifiedFoldersCapped?: true;
  classified?: Array<{
    filePath: string;
    layer: string;
    confidence: number;
    signals: string[];
    role?: 'controller' | 'service' | 'repository' | 'entity' | 'handler' | 'router';
  }>;
  workspaceShape?: 'single' | 'monorepo' | 'polyglot-platform';
  supportLevel?: 0 | 1 | 2 | 3 | 4;
  packIds?: string[];
  projectKind?: string;
  artifactKind?: string;
  contractKind?: string;
  semanticRole?: string;
  evidence?: ArchitectureEvidenceWire[];
  evidenceCapped?: true;
  workspaceEdges?: ArchitectureWorkspaceEdgeWire[];
  workspaceEdgesCapped?: true;
  violations?: ArchitectureViolationWire[];
  violationsCapped?: true;
  boundaryProfile?: string;
  structure?: ArchitectureStructureWire;
  generated?: true;
  generatedBy?: string;
  sourceContract?: string;
}): ArchitectureResponse {
  const out: ArchitectureResponse = {
    archetype: arch.archetype,
    archetypeConfidence: arch.archetypeConfidence,
    layers: arch.layers,
    totalClassified: arch.totalClassified,
    unclassified: arch.unclassified,
  };
  if (arch.folders) out.folders = arch.folders;
  if (arch.unclassifiedFiles) out.unclassifiedFiles = arch.unclassifiedFiles;
  if (arch.coverage) out.coverage = arch.coverage;
  if (arch.unclassifiedFolders) out.unclassifiedFolders = arch.unclassifiedFolders;
  if (arch.unclassifiedFoldersCapped) out.unclassifiedFoldersCapped = true;
  if (arch.classified) out.classified = arch.classified;
  if (arch.workspaceShape) out.workspaceShape = arch.workspaceShape;
  if (arch.supportLevel !== undefined) out.supportLevel = arch.supportLevel;
  if (arch.packIds) out.packIds = arch.packIds;
  if (arch.projectKind) out.projectKind = arch.projectKind;
  if (arch.artifactKind) out.artifactKind = arch.artifactKind;
  if (arch.contractKind) out.contractKind = arch.contractKind;
  if (arch.semanticRole) out.semanticRole = arch.semanticRole;
  if (arch.evidence) out.evidence = arch.evidence;
  if (arch.evidenceCapped) out.evidenceCapped = true;
  if (arch.workspaceEdges) out.workspaceEdges = arch.workspaceEdges;
  if (arch.workspaceEdgesCapped) out.workspaceEdgesCapped = true;
  if (arch.violations) out.violations = arch.violations;
  if (arch.violationsCapped) out.violationsCapped = true;
  if (arch.boundaryProfile) {
    out.boundaryProfile = arch.boundaryProfile;
    const rules = boundaryProfileRules(arch.boundaryProfile);
    if (rules.length > 0) out.boundaryRules = rules;
  }
  if (arch.structure) out.structure = arch.structure;
  if (arch.generated) out.generated = true;
  if (arch.generatedBy) out.generatedBy = arch.generatedBy;
  if (arch.sourceContract) out.sourceContract = arch.sourceContract;
  return out;
}

/**
 * Tech-stack group labels, in render order. The artifact's keys are camelCase
 * field names; these are what a reader sees.
 */
const TECH_STACK_LABELS: Array<[keyof ToolingInventoryResult, string]> = [
  ['frontend', 'Frontend'],
  ['metaFrameworks', 'Meta-frameworks'],
  ['bundlers', 'Bundlers'],
  ['css', 'CSS'],
  ['backend', 'Backend'],
  ['orm', 'ORM'],
  ['testing', 'Testing'],
  ['lintFormat', 'Lint & format'],
  ['apiMessaging', 'API & messaging'],
  ['observability', 'Observability'],
];

/**
 * Project the tooling inventory into labelled groups. Returns `undefined`
 * — not `[]` — when the scanner produced nothing, so the client can tell
 * "not scanned" from "scanned, nothing found".
 */
function techStackGroups(inventory: ToolingInventoryResult | undefined): TechStackGroupWire[] | undefined {
  if (!inventory) return undefined;
  const groups: TechStackGroupWire[] = [];
  for (const [key, label] of TECH_STACK_LABELS) {
    const items = inventory[key];
    if (!items?.length) continue;
    groups.push({ label, items: items.map((i) => ({ name: i.name, version: i.version })) });
  }
  return groups.length > 0 ? groups : undefined;
}

/**
 * One row of the workspace-scope Projects view — the per-project architecture
 * facts a client needs to list every project side by side without issuing an
 * `vibgrate/architecture` request per project. Pure projection of data the
 * scan already produced; nothing here is computed for the client.
 */
export interface ArchitectureProjectSummary {
  path: string;
  name: string;
  projectKind?: string;
  artifactKind?: string;
  /** `structure.primary` — the detected shape (clean, layered, …). */
  structure?: string;
  supportLevel?: 0 | 1 | 2 | 3 | 4;
  /** Number of boundary violations. `null` when this project was never checked. */
  violations: number | null;
  packIds?: string[];
  /** Layers carrying at least one file. */
  layerCount: number;
  fileCount: number;
}

/** Per-layer summary, wire shape mirrors `LayerSummary` in core-open/types.ts. */
export interface LayerSummaryWire {
  layer: string;
  fileCount: number;
  driftScore: number;
  riskLevel: 'none' | Band;
  techStack: Array<{ name: string; package: string; version: string | null }>;
  services: Array<{ name: string; package: string; version: string | null }>;
  packages: Array<{
    name: string;
    version: string | null;
    latestStable: string | null;
    majorsBehind: number | null;
    drift: 'current' | 'minor-behind' | 'major-behind' | 'unknown';
  }>;
  /** Workspace- or project-relative sample file paths classified into this layer. */
  files: string[];
}

/** One runtime's real EOL date and distance from today, for the panel's horizon. */
export interface EolHorizonItem {
  /** Human label, e.g. "Node.js 18" — not the raw project path. */
  runtime: string;
  /** ISO end-of-life date from the Runtime Catalog (endoflife.date). */
  eolDate: string;
  /** Negative = already past EOL by this many days. */
  daysUntil: number;
}

/** Manifest hit for a dependency (workspace-relative path + optional line). */
export interface BreakdownLocation {
  file: string;
  /** 1-based line of the package in the manifest, when known. */
  line: number | null;
}

/** One dependency's contribution to the score, for the panel accordion. */
export interface BreakdownItem {
  package: string;
  section: string;
  /**
   * OSV.dev ecosystem slug for this dependency (npm, PyPI, Go, Maven, crates.io,
   * RubyGems, NuGet, Packagist, Pub, …), from the owning project's type. Null
   * when the ecosystem has no OSV mapping — the client then skips enrichment.
   */
  ecosystem: string | null;
  /** 0–100 drift contribution for this dependency. */
  drift: number;
  /** Band for `drift` — the client maps it to a colour; it never re-derives it. */
  band: Band;
  currentSpec: string;
  resolvedVersion: string | null;
  latestStable: string | null;
  majorsBehind: number | null;
  ageDays: number | null;
  mode: 'verified' | 'estimated';
  unsupported: boolean;
  abandoned: boolean;
  /** Data-quality guards that fired (canary-latest, scheme-jump, high-cadence…). */
  flags: string[];
  /**
   * Every project manifest that pins this package — the Dependencies panel
   * lists these as open-in-editor links (same pattern as the Vulnerabilities panel).
   */
  locations: BreakdownLocation[];
}

/** One coloured end-of-line decoration. `vibgrate/inline`, pushed per open manifest. */
export interface InlineItem {
  line: number;
  endCol: number;
  text: string;
  band: Band;
  package: string;
  /** Actively ignored via `.vibgrate/ignore.json` — calmed, never hidden. */
  ignored?: boolean;
  /** Drifted now but not in the previous recorded scan (same methodology). */
  newSincePrev?: boolean;
}

export interface InlineNotification {
  uri: string;
  items: InlineItem[];
}

/**
 * `vibgrate/status` — a scan lifecycle signal that carries no score. Currently
 * only `error`, pushed when a scan fails, so a client never sits on a "scanning"
 * placeholder forever. Deliberately minimal and non-alarming (the client shows a
 * quiet state, never a toast).
 */
export interface StatusNotification {
  state: 'error';
}

/**
 * `vibgrate/graph/status` — mirrors `vibgrate/status` but for the code map:
 * lets a client show a "building the map…" placeholder for the Graph section
 * the first time it runs on a repo, without blocking the drift score.
 */
export interface GraphStatusNotification {
  /** `disabled` = the client launched with `--no-graph` (the Graph setting is off). */
  state: 'building' | 'ready' | 'error' | 'disabled';
  message?: string;
}

// ── Band + severity mapping ────────────────────────────────────────────────

/**
 * Per-dependency band, from the engine's drift classification.
 *
 * Score and badge bands are one system (driftscore-3.0 §6.5): low/green ≤30 ·
 * moderate/amber ≤60 · high/red >60. Clients must still not re-derive a band
 * from a bare number — always use the engine's `band` / classification so a
 * future threshold change stays in one place.
 */
function bandForDependency(dep: DependencyRow): Band {
  if (dep.drift === 'major-behind') {
    return (dep.majorsBehind ?? 1) >= 2 ? 'high' : 'moderate';
  }
  if (dep.drift === 'minor-behind') return 'moderate';
  return 'low';
}

/**
 * The engine emits `error` for an EOL runtime. The editor must not.
 * See the file header, rule 2.
 */
function severityFor(level: 'warning' | 'error' | 'note'): 1 | 2 | 3 | 4 {
  // LSP DiagnosticSeverity: 1 Error · 2 Warning · 3 Information · 4 Hint
  if (level === 'note') return 3;
  return 2; // both `warning` and `error` clamp to Warning. Never 1.
}

/**
 * Only hard facts reach the Problems panel, and only when the user opts in.
 * "You are two minors behind" is not a defect and does not belong here
 * (plan §8.2).
 */
const DIAGNOSTIC_RULES = new Set([
  'vibgrate/runtime-eol',
  'vibgrate/runtime-lag',
  'vibgrate/unmaintained',
  'vibgrate/abandoned',
  'vibgrate/license-change',
  'vibgrate/eol',
]);

// ── Server ─────────────────────────────────────────────────────────────────

export interface ServerOptions {
  root: string;
  offline: boolean;
  /** Problems-panel diagnostics. OFF unless the client asks (plan §8.1). */
  diagnostics: boolean;
  /** The local Vibgrate Graph. `false` (`--no-graph`) skips the background build and disables graph queries. */
  graph: boolean;
  /** Semantic search over the graph. `false` (`--no-semantic`) forces lexical and never downloads the embedding model. */
  semantic: boolean;
  /** `--no-daemon`: never auto-start or use the local runtime (vgd). */
  daemon?: boolean;
  /**
   * Talk to this vgd socket instead of the default. Tests and custom runtimes
   * use it so a language server can own a daemon without racing another.
   */
  socketPath?: string;
}

export class VibgrateLanguageServer {
  private readonly conn: Connection;
  private readonly docs = new Map<string, string>(); // uri → text
  private artifact: ScanArtifact | null = null;
  /** Active `.vibgrate/ignore.json` entries, refreshed once per scan. */
  private ignores = new Map<string, IgnoreEntry>();
  /** `project.path::package` drifted now but not in the previous snapshot. */
  private newDrift = new Set<string>();
  private scanning = false;
  private queued = false;
  /**
   * Sticky until the next `scan()` runs: set by explicit "Rescan Workspace"
   * (and preserved if a non-force schedule races the debounce). Bypasses the
   * LSP scan cache so config/SKIP_DIRS changes actually re-score.
   */
  private forceNextScan = false;
  private debounce: NodeJS.Timeout | null = null;
  private shuttingDown = false;
  private graph: VgGraph | null = null;
  /** Role hits from the last graph build or recovered from the parse cache. */
  private fileRoles: AstRoleHit[] = [];
  private graphBuilding = false;
  /**
   * Keeps the freshness probe / incremental rebuild OFF the query path. The
   * probe alone stat-walks the whole corpus synchronously (seconds on a 20k-file
   * workspace) and a real drift rebuilds the map; awaiting either inline made a
   * single Ask block for over a minute. See engine/refresh-scheduler.ts.
   */
  private readonly refresher: RefreshScheduler;
  /**
   * Open while the daemon is pushing map changes for this workspace. Closing
   * it (or losing it) puts this server back in charge of its own freshness.
   */
  private slotSubscription: { close(): void } | null = null;
  /**
   * True only while the daemon is confirmed to hold *this* map and to be
   * pushing changes for it. While set, `graphForQuery` stops probing: the
   * daemon already watches the tree and rebuilds, and two processes doing it
   * means two stat-walks over one corpus and two rebuilds racing for one lock.
   *
   * Never assumed — see `deferFreshnessToDaemon`. A daemon that dies degrades
   * this server to watching for itself, never to nothing watching.
   */
  private daemonOwnsFreshness = false;
  /**
   * Slot the daemon is serving for this workspace. When set, this process
   * holds **no** `VgGraph` — queries go over the socket, and a slot-changed
   * push updates this summary rather than reloading the map from disk.
   */
  private daemonSlot: {
    repositoryId: string;
    gitRef: string;
    socketPath?: string;
    nodeCount: number;
    corpusHash: string | null;
  } | null = null;
  /**
   * The local runtime's semantic index, held for the life of the server.
   * Ranking there means the native embedding backend is never loaded into this
   * process — the failure this server has the least tolerance for, since a
   * SIGTRAP from the addon kills it outright mid-request.
   */
  private readonly semanticSession: DaemonSemanticSession;
  /** In-flight on-demand scans of open manifests the workspace scan skipped. */
  private readonly openManifestScans = new Set<string>();
  /** Failed on-demand scans — retry only after the next workspace scan. */
  private readonly openManifestFailed = new Set<string>();

  constructor(
    private readonly opts: ServerOptions,
    output: NodeJS.WritableStream = process.stdout,
    input: NodeJS.ReadableStream = process.stdin,
  ) {
    this.conn = new Connection(input, output);
    this.semanticSession = new DaemonSemanticSession(opts.root, {
      disabled: opts.daemon === false,
      socketPath: opts.socketPath,
      autoStart: opts.socketPath ? false : undefined,
      env: opts.socketPath ? envForNamedVgdSocket() : undefined,
    });
    this.refresher = new RefreshScheduler({
      root: opts.root,
      graphPath: () => resolveGraphPath(opts.root),
      onSettled: (outcome) => this.onRefreshSettled(outcome),
    });
    this.register();
  }

  private register(): void {
    this.conn.onRequest('initialize', () => ({
      capabilities: {
        // 1 = Full sync. We only actually act on save, but full sync keeps our
        // buffer honest so hovers land on the text the user is looking at.
        textDocumentSync: 1,
        hoverProvider: true,
        codeLensProvider: { resolveProvider: false },
        executeCommandProvider: {
          commands: ['vibgrate.rescan', 'vibgrate.explain', 'vibgrate.routeFix'],
        },
      },
      serverInfo: { name: 'Vibgrate', version: VERSION },
    }));

    this.conn.onNotification('initialized', () => {
      this.scheduleScan(0);
      // Independent of the drift scan — a slow first graph build must not
      // delay the score the status bar/panel are waiting on.
      void this.ensureGraph();
    });

    this.conn.onNotification('textDocument/didOpen', (_m, params) => {
      const p = params as { textDocument?: { uri?: string; text?: string } };
      const uri = p.textDocument?.uri;
      if (!uri || !isManifest(uriToPath(uri))) return;
      this.docs.set(uri, p.textDocument?.text ?? '');
      // Already scanned? Render immediately — don't make the user wait on a
      // rescan just because they opened a second manifest.
      if (this.artifact) this.publishForDoc(uri);
      else this.scheduleScan(0);
    });

    this.conn.onNotification('textDocument/didChange', (_m, params) => {
      const p = params as {
        textDocument?: { uri?: string };
        contentChanges?: { text?: string }[];
      };
      const uri = p.textDocument?.uri;
      if (!uri || !this.docs.has(uri)) return;
      const full = p.contentChanges?.[p.contentChanges.length - 1]?.text;
      if (typeof full === 'string') this.docs.set(uri, full);
      // Do NOT rescan on keystroke — that is how you get a tool that heats a
      // laptop. Re-anchor the existing decorations against the new text only.
      if (this.artifact) this.publishForDoc(uri);
    });

    this.conn.onNotification('textDocument/didSave', (_m, params) => {
      const p = params as { textDocument?: { uri?: string } };
      const uri = p.textDocument?.uri;
      if (!uri || !this.docs.has(uri)) return;
      this.scheduleScan(400);
    });

    // External writers — a terminal `vg fix`, a `git checkout`, a package
    // manager install — change manifests without ever producing a `didSave`
    // (that only fires for saves made inside the editor). Without this, the
    // last artifact's decorations stay anchored to the updated text and the
    // editor keeps showing "behind" on a line that is now current. The client
    // watches dependency files and forwards changes here; a false positive is
    // near-free because `scan()` gates on the manifest hash and replays the
    // cache when nothing a scan reads actually changed.
    this.conn.onNotification('workspace/didChangeWatchedFiles', (_m, params) => {
      const p = params as { changes?: { uri?: string }[] };
      const relevant = (p.changes ?? []).some(
        (c) => c.uri && isDependencyFile(this.opts.root, uriToPath(c.uri)),
      );
      if (relevant) this.scheduleScan(400); // same debounce as didSave
    });

    this.conn.onNotification('textDocument/didClose', (_m, params) => {
      const p = params as { textDocument?: { uri?: string } };
      if (p.textDocument?.uri) this.docs.delete(p.textDocument.uri);
    });

    this.conn.onRequest('textDocument/hover', (_m, params) => this.onHover(params));
    this.conn.onRequest('textDocument/codeLens', (_m, params) => this.onCodeLens(params));

    this.conn.onRequest('vibgrate/graph/query', (_m, params) => this.onGraphQuery(params));
    this.conn.onRequest('vibgrate/score/forFile', (_m, params) => this.onScoreForFile(params));
    this.conn.onRequest('vibgrate/score/forProject', (_m, params) => this.onScoreForProject(params));
    this.conn.onRequest('vibgrate/architecture', (_m, params) => this.onArchitecture(params));
    this.conn.onRequest('vibgrate/architecture/projects', () => this.onArchitectureProjects());
    this.conn.onRequest('vibgrate/architecture/page', (_m, params) => this.onArchitecturePage(params));
    this.conn.onRequest('vibgrate/architecture/slice', (_m, params) => this.onArchitectureSlice(params));
    this.conn.onRequest('vibgrate/architecture/node', (_m, params) => this.onArchitectureNode(params));
    this.conn.onRequest('vibgrate/architecture/ui', () => this.onArchitectureUi());
    this.conn.onRequest('vibgrate/architecture/layout', (_m, params) => this.onArchitectureLayout(params));
    // Trend data for the panel sparkline (plan §5.6). Entries carry their
    // methodology so the client can break the line across a change — the one
    // trend rule v3 imposes. Empty history is an empty array, not an error.
    this.conn.onRequest('vibgrate/score/history', (_m, params) => {
      const p = params as { days?: number } | undefined;
      const days = typeof p?.days === 'number' && p.days > 0 && p.days <= 365 ? p.days : 90;
      return { days, entries: recentHistory(this.opts.root, days) };
    });
    this.conn.onRequest('vibgrate/enrich', (_m, params) => this.onEnrich(params));
    this.conn.onRequest('vibgrate/vulns', (_m, _params) => this.onVulns());

    this.conn.onRequest('workspace/executeCommand', (_m, params) => {
      const p = params as { command?: string };
      if (p.command === 'vibgrate.rescan') {
        // Explicit "Rescan Workspace" must re-run the engine, not replay the
        // LSP scan cache (config/exclude/SKIP_DIRS changes don't alter the
        // manifest hash, so a cache hit would look like "nothing happened").
        this.scheduleScan(0, { force: true });
        return { ok: true };
      }
      return null;
    });

    this.conn.onRequest('shutdown', () => {
      this.shuttingDown = true;
      if (this.debounce) clearTimeout(this.debounce);
      this.slotSubscription?.close();
      this.slotSubscription = null;
      this.daemonOwnsFreshness = false;
      this.daemonSlot = null;
      return null;
    });

    this.conn.onNotification('exit', () => {
      process.exit(this.shuttingDown ? 0 : 1);
    });
  }

  // ── Scanning ─────────────────────────────────────────────────────────────

  private scheduleScan(delayMs: number, opts?: { force?: boolean }): void {
    if (opts?.force) this.forceNextScan = true;
    if (this.debounce) clearTimeout(this.debounce);
    this.debounce = setTimeout(() => void this.scan(), delayMs);
  }

  private async scan(): Promise<void> {
    if (this.scanning) {
      this.queued = true;
      return;
    }
    this.scanning = true;
    const force = this.forceNextScan;
    this.forceNextScan = false;
    this.openManifestFailed.clear();

    try {
      // The expensive part of a scan is the per-package registry lookups, not
      // reading the manifest/lockfile — so hash just those first (cheap: no
      // network) and skip `runCoreScan` entirely when nothing a scan cares
      // about has changed since the last successful scan on this machine.
      // `toolVersion`/`offline` are part of the key too: an engine upgrade or a
      // flip of `vibgrate.offline` must not replay a scan from before it.
      // Explicit Rescan Workspace sets `forceNextScan` and always re-runs.
      // Prefer the daemon's digest: it walks the manifest tree and hashes
      // every manifest (~13ms here) and would otherwise do so once per process
      // that wants it. The daemon computes it once and drops it when its own
      // watcher sees a manifest write, so the key is no staler than ours was.
      const cacheKey = {
        manifestHash: (await this.sharedManifestHash()) ?? manifestHash(this.opts.root),
        toolVersion: VERSION,
        offline: this.opts.offline,
      };
      const cached = force ? null : loadScanCache(this.opts.root, cacheKey);

      let fromCache = false;
      if (cached) {
        // Drop projects under pruned trees (e.g. `.claude/worktrees`) even when
        // replaying a cache written before those dirs were excluded.
        this.artifact = pruneArtifactProjects(cached.artifact);
        this.refineArchitectureInPlace();
        fromCache = true;
      } else {
        const scanOpts: ScanOptions = {
          format: 'json',
          concurrency: 8,
          offline: this.opts.offline,
          noLocalArtifacts: true, // an editor scan must not litter the repo
          noGraph: true, // the code map is not needed to score drift; skip the cost
          quiet: true, // no spinners, no artifact dump — an editor is not a terminal
          vibgrateVersion: VERSION,
        } as ScanOptions;

        // Same hook `vg scan` uses, so the number the editor shows is the number
        // the CLI shows — by construction, not by coincidence (plan §8.3).
        //
        // `format: 'json'` makes runCoreScan console.log the full prettified
        // artifact. In the LSP process stdout is redirected to stderr, and the
        // extension used to dump that into Output ▸ Vibgrate — drowning the
        // operational log. Suppress that dump here; we re-emit the same JSON
        // on `vibgrate/scanArtifact` for Output ▸ Vibgrate Scan instead.
        const advanced = await loadAdvancedScanHook();
        this.artifact = pruneArtifactProjects(
          await runCoreScanSilently(this.opts.root, scanOpts, advanced, this.graph, this.fileRoles),
        );
        writeScanCache(this.opts.root, cacheKey, this.artifact);
      }

      // Graph / AST-role refine can land after the first scan. Re-refine so
      // roles recovered (or extracted) after we started still land.
      this.refineArchitectureInPlace();

      this.publishScore();
      this.publishScanArtifact(fromCache);
      for (const uri of this.docs.keys()) this.publishForDoc(uri);
    } catch (err) {
      // A failed scan is silent — no red banner because a lockfile was mid-write.
      // But it must not be INVISIBLE: without a signal the client sits on its
      // "scanning…" placeholder forever. The reason goes to the log; a minimal
      // `error` status tells the client to stop pretending it is still working.
      this.conn.notify('window/logMessage', {
        type: 3,
        message: `Vibgrate scan failed: ${err instanceof Error ? err.message : String(err)}`,
      });
      this.conn.notify('vibgrate/status', { state: 'error' } satisfies StatusNotification);
    } finally {
      this.scanning = false;
      if (this.queued) {
        this.queued = false;
        // forceNextScan may have been set while we were busy; keep it sticky.
        this.scheduleScan(0);
      }
    }
  }

  // ── Publishing ───────────────────────────────────────────────────────────

  /**
   * Full scan artifact for Output ▸ Vibgrate Scan. Operational messages stay
   * on window/logMessage → Output ▸ Vibgrate.
   */
  private publishScanArtifact(fromCache: boolean): void {
    const a = this.artifact;
    if (!a) return;
    this.conn.notify('vibgrate/scanArtifact', {
      json: JSON.stringify(a, null, 2),
      rootPath: a.rootPath,
      scannedAt: a.timestamp,
      ...(fromCache ? { fromCache: true } : {}),
    } satisfies ScanArtifactNotification);
  }

  private publishScore(): void {
    const a = this.artifact;
    if (!a) return;

    let behind = 0;
    let unmaintained = 0;
    let total = 0;
    for (const proj of a.projects ?? []) {
      for (const dep of proj.dependencies ?? []) {
        total++;
        if (dep.drift === 'major-behind' || dep.drift === 'minor-behind') behind++;
      }
    }
    const eol = (a.findings ?? []).filter(
      (f) => f.ruleId === 'vibgrate/runtime-eol' || f.ruleId === 'vibgrate/eol',
    ).length;
    unmaintained = (a.findings ?? []).filter(
      (f) => f.ruleId === 'vibgrate/unmaintained' || f.ruleId === 'vibgrate/abandoned',
    ).length;

    // `riskLevel` is what `driftscore-2.0` ships; v3 renames it `band` (§5
    // envelope). We normalise to `band` on the wire so clients are already
    // speaking v3 and need no change when the engine catches up.
    const band = (a.drift.riskLevel ?? 'low') as Band;

    // History + drift diff (plan §5.6/§5.8): diff against the last recorded
    // entry, then record this one. Both engine-side — clients only render.
    const historyEntry: ScoreHistoryEntry = {
      ts: a.timestamp,
      score: a.drift.score,
      band,
      mode: a.drift.mode ?? (hasReleaseDates(a) ? 'verified' : 'estimated'),
      methodology: a.drift.methodologyVersion ?? 'unknown',
    };
    const delta = deltaFrom(lastEntry(this.opts.root), historyEntry);
    recordScore(this.opts.root, historyEntry);

    // Per-dependency state for the inline/hover surfaces — all O(deps), once
    // per scan, never in a hover or decoration hot path (coverage plan §5).
    this.ignores = readIgnores(this.opts.root);
    const driftedKeys: string[] = [];
    for (const proj of a.projects ?? []) {
      for (const dep of proj.dependencies ?? []) {
        if (dep.drift === 'major-behind' || dep.drift === 'minor-behind') {
          driftedKeys.push(inventoryKey(proj.path, dep.package));
        }
      }
    }
    const snapshot = { ts: a.timestamp, methodology: historyEntry.methodology, drifted: driftedKeys };
    this.newDrift = newlyDrifted(readInventory(this.opts.root), snapshot);
    recordInventory(this.opts.root, snapshot);

    const payload: ScoreNotification = {
      score: a.drift.score,
      band,
      // v3 §2.4: `estimated` means "no timestamps at all" — it is NOT an
      // offline marker. An air-gapped scan against a dated snapshot is Verified.
      // The score now carries the authoritative provenance (driftscore-3.0
      // envelope); fall back to the artifact heuristic for pre-v3 engines.
      mode: historyEntry.mode,
      methodology: historyEntry.methodology,
      scale: '0 best, 100 worst',
      counts: { behind, eol, unmaintained, total },
      rootPath: a.rootPath,
      scannedAt: a.timestamp,
      ...(delta !== undefined ? { delta } : {}),
      ...(buildBreakdown(this.opts.root, a.projects ?? [])
        ? { breakdown: buildBreakdown(this.opts.root, a.projects ?? []) }
        : {}),
      ...(buildEolHorizon(a.projects ?? []) ? { eolHorizon: buildEolHorizon(a.projects ?? []) } : {}),
      ...(buildProjectRefs(this.opts.root, a.projects ?? [])
        ? { projects: buildProjectRefs(this.opts.root, a.projects ?? []) }
        : {}),
    };

    this.conn.notify('vibgrate/score', payload);
  }

  /** Decorations + (optionally) diagnostics for one open manifest. */
  private publishForDoc(uri: string): void {
    const a = this.artifact;
    const text = this.docs.get(uri);
    if (!a || text === undefined) return;

    const filePath = uriToPath(uri);
    const kind = manifestKind(filePath);
    const project = projectForFile(this.opts.root, a, filePath);
    // A nested package.json the workspace scan skipped (auto-exclude / timeout)
    // used to inherit the parent project's deps. Those names are not in this
    // file, so the overlay stayed empty while Explorer still showed the
    // parent's DriftScore. Scan just this package so the dots land here.
    if (!project && isManifest(filePath)) {
      void this.scanOpenManifest(uri, filePath);
    }

    // ── inline decorations ──
    const items: InlineItem[] = [];
    for (const dep of project?.dependencies ?? []) {
      if (dep.drift === 'current' || dep.drift === 'unknown') continue;
      const line = findPackageLine(text, dep.package, kind);
      if (line === -1) continue;
      const ignored = this.ignores.get(dep.package.toLowerCase());
      const isNew = project ? this.newDrift.has(inventoryKey(project.path, dep.package)) : false;
      items.push({
        line,
        endCol: endOfLine(text, line),
        text: inlineLabel(dep, { ignored: !!ignored, isNew }),
        // An acknowledged ignore stops shouting: the engine calms the band to
        // low (presentation only — the score is untouched). The client still
        // never derives a band; it renders this one.
        band: ignored ? 'low' : bandForDependency(dep),
        package: dep.package,
        ...(ignored ? { ignored: true } : {}),
        ...(isNew ? { newSincePrev: true } : {}),
      });
    }
    this.conn.notify('vibgrate/inline', { uri, items } satisfies InlineNotification);

    // ── diagnostics (off unless the client asked) ──
    if (!this.opts.diagnostics) {
      this.conn.notify('textDocument/publishDiagnostics', { uri, diagnostics: [] });
      return;
    }

    const diagnostics = (a.findings ?? [])
      .filter((f) => DIAGNOSTIC_RULES.has(f.ruleId))
      .filter((f) => project && sameProject(f.location, project.path))
      // A per-package finding for an actively ignored dependency stays out of
      // the shared Problems panel; the calmed inline label still marks it.
      .filter((f) => {
        const pkg = f.details?.['package'];
        return !(typeof pkg === 'string' && this.ignores.has(pkg.toLowerCase()));
      })
      .map((f) => {
        const line = Math.max(0, findRuntimeLine(text, kind));
        return {
          range: {
            start: { line, character: 0 },
            end: { line, character: endOfLine(text, line) },
          },
          severity: severityFor(f.level), // never 1 — see file header
          source: 'vibgrate',
          code: f.ruleId,
          message: f.message,
        };
      });

    this.conn.notify('textDocument/publishDiagnostics', { uri, diagnostics });
  }

  /**
   * Workspace scan skipped this package (auto-exclude / timeout). Score it
   * alone so the open editor can show overlays; do not write the result into
   * the workspace scan cache (billing / repo score stay on the official scan).
   */
  private async scanOpenManifest(uri: string, filePath: string): Promise<void> {
    const relDir = path.relative(this.opts.root, path.dirname(path.resolve(filePath)));
    const key = relDir === '' ? '.' : relDir;
    if (this.openManifestScans.has(key) || this.openManifestFailed.has(key)) return;
    if (key === '.' || key.startsWith('..')) return;
    this.openManifestScans.add(key);
    try {
      this.conn.notify('window/logMessage', {
        type: 3,
        message: `Vibgrate: scoring open package ${key} (not in the workspace scan) so editor overlays can render.`,
      });
      const advanced = await loadAdvancedScanHook();
      const scanOpts: ScanOptions = {
        format: 'json',
        concurrency: 8,
        offline: this.opts.offline,
        noLocalArtifacts: true,
        noGraph: true,
        quiet: true,
        vibgrateVersion: VERSION,
      } as ScanOptions;
      const sub = await runCoreScanSilently(path.dirname(filePath), scanOpts, advanced);
      const proj = (sub.projects ?? []).find((p) => p.path === '.' || p.path === '') ?? sub.projects?.[0];
      if (!proj || !this.artifact) {
        this.openManifestFailed.add(key);
        return;
      }
      proj.path = key;
      this.artifact.projects = [...(this.artifact.projects ?? []).filter((p) => p.path !== key), proj];
      for (const f of sub.findings ?? []) {
        if (f.location === '.' || f.location === '' || f.location === key) {
          this.artifact.findings = [...(this.artifact.findings ?? []).filter((x) => !(x.ruleId === f.ruleId && x.location === key)), { ...f, location: key }];
        }
      }
      this.publishScore();
      this.publishForDoc(uri);
    } catch (err) {
      this.openManifestFailed.add(key);
      const message = err instanceof Error ? err.message : String(err);
      this.conn.notify('window/logMessage', {
        type: 3,
        message: `Vibgrate: could not score open package ${key}: ${message}`,
      });
    } finally {
      this.openManifestScans.delete(key);
    }
  }

  // ── Requests ─────────────────────────────────────────────────────────────

  private onHover(params: unknown): unknown {
    const p = params as {
      textDocument?: { uri?: string };
      position?: { line?: number; character?: number };
    };
    const uri = p.textDocument?.uri;
    const line = p.position?.line;
    const a = this.artifact;
    const text = uri ? this.docs.get(uri) : undefined;
    if (!uri || line === undefined || !a || text === undefined) return null;

    const filePath = uriToPath(uri);
    const kind = manifestKind(filePath);
    const project = projectForFile(this.opts.root, a, filePath);

    const dep = (project?.dependencies ?? []).find(
      (d) => findPackageLine(text, d.package, kind) === line,
    );
    if (!dep) {
      // Not a dependency line — the runtime line answers with the EOL card
      // (coverage plan §6 P1-5): real endoflife.date-sourced dates, no field
      // competitor hovers here at all.
      if (project && line === Math.max(0, findRuntimeLine(text, kind))) {
        const value = runtimeHoverMarkdown(project, a.findings ?? []);
        return value ? { contents: { kind: 'markdown', value } } : null;
      }
      return null;
    }

    const contribution = a.drift.dependencyDrift?.top.find((t) => t.package === dep.package)?.drift ?? null;
    const ignored = this.ignores.get(dep.package.toLowerCase());
    const isNew = project ? this.newDrift.has(inventoryKey(project.path, dep.package)) : false;
    return {
      contents: { kind: 'markdown', value: hoverMarkdown(dep, contribution, { ignored, isNew }) },
    };
  }

  private onCodeLens(params: unknown): unknown {
    const p = params as { textDocument?: { uri?: string } };
    const uri = p.textDocument?.uri;
    const a = this.artifact;
    if (!uri || !a || !this.docs.has(uri)) return [];

    // ONE lens, at the top of the file. A per-dependency lens would double the
    // height of package.json — and a CodeLens cannot carry colour anyway, which
    // is why the per-dependency detail is an inline decoration (plan §5.2).
    const filePath = uriToPath(uri);
    const project = projectForFile(this.opts.root, a, filePath);
    if (!project) {
      return [
        {
          range: { start: { line: 0, character: 0 }, end: { line: 0, character: 0 } },
          command: {
            title: 'Vibgrate · this package is not in the workspace scan',
            command: 'vibgrate.openPanel',
            arguments: [],
          },
        },
      ];
    }
    const behind = (project.dependencies ?? []).filter(
      (d) => d.drift === 'major-behind' || d.drift === 'minor-behind',
    ).length;
    const eol = (a.findings ?? []).filter(
      (f) =>
        sameProject(f.location, project.path) &&
        (f.ruleId === 'vibgrate/runtime-eol' || f.ruleId === 'vibgrate/eol'),
    ).length;

    const estimated =
      (project.drift?.mode ?? a.drift.mode ?? (hasReleaseDates(a) ? 'verified' : 'estimated')) ===
      'estimated';
    const mode = estimated ? '~' : '';
    const score = project.drift?.score ?? a.drift.score;
    const band = project.drift?.riskLevel ?? a.drift.riskLevel;
    const title = `Vibgrate · drift ${mode}${score} (${band}) · ${behind} behind · ${eol} EOL`;

    return [
      {
        range: { start: { line: 0, character: 0 }, end: { line: 0, character: 0 } },
        command: { title, command: 'vibgrate.openPanel', arguments: [] },
      },
    ];
  }

  // ── Graph ────────────────────────────────────────────────────────────────

  /**
   * Make a code map available for queries. When a daemon is running it is the
   * **only** process that holds the graph — this server stores a slot id and
   * talks to vgd. Local load/build is the `--no-daemon` / no-runtime fallback.
   */
  private async ensureGraph(): Promise<void> {
    if (!this.opts.graph) {
      // Turned off by the user (`--no-graph`). Say so once, quietly — the
      // client's Graph section renders the off state instead of "building…".
      this.conn.notify('vibgrate/graph/status', { state: 'disabled' } satisfies GraphStatusNotification);
      return;
    }
    if (this.daemonSlot || this.graph || this.graphBuilding) return;
    this.graphBuilding = true;
    try {
      if (this.opts.daemon !== false && (await this.takeGraphFromDaemon())) return;
      await this.ensureGraphLocal();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.conn.notify('window/logMessage', { type: 3, message: `Vibgrate graph build failed: ${message}` });
      this.conn.notify('vibgrate/graph/status', { state: 'error', message } satisfies GraphStatusNotification);
    } finally {
      this.graphBuilding = false;
    }
  }

  /**
   * Hand the map to vgd (load or rebuild in a child) and subscribe. Returns
   * true when a daemon answered — including "rebuild failed" — so this process
   * never also `buildGraph`s. False means no runtime, so the caller falls back.
   */
  private async takeGraphFromDaemon(): Promise<boolean> {
    try {
      const [{ attachVgd }, { vgdRequest }, { subscribeToSlots }] = await Promise.all([
        import('../runtime/vgd/attach.js'),
        import('../runtime/vgd/client.js'),
        import('../runtime/vgd/slot-subscription.js'),
      ]);
      const attached = await attachVgd(this.opts.root, {
        autoStart: this.opts.socketPath ? false : true,
        publish: false,
        disabled: this.opts.daemon === false,
        socketPath: this.opts.socketPath,
        env: this.opts.socketPath ? envForNamedVgdSocket() : undefined,
      });
      if (attached.status !== 'attached' || !attached.socketPath) return false;

      this.conn.notify('vibgrate/graph/status', { state: 'building' } satisfies GraphStatusNotification);
      const ensured = await vgdRequest(
        { op: 'ensure-graph', root: this.opts.root, graphPath: resolveGraphPath(this.opts.root) },
        { socketPath: attached.socketPath },
      );
      if (!ensured.ok || !('stored' in ensured)) {
        const message = !ensured.ok ? ensured.error : 'the local runtime did not store a code map';
        this.conn.notify('window/logMessage', { type: 3, message: `Vibgrate graph: ${message}` });
        this.conn.notify('vibgrate/graph/status', { state: 'error', message } satisfies GraphStatusNotification);
        return true;
      }

      let corpusHash: string | null = null;
      try {
        const summary = await vgdRequest(
          { op: 'graph-summary', repositoryId: ensured.repositoryId, gitRef: ensured.gitRef },
          { socketPath: attached.socketPath },
        );
        if (summary.ok && 'summary' in summary) corpusHash = summary.summary.corpusHash;
      } catch {
        /* summary is diagnostic — the slot is what matters */
      }

      this.daemonSlot = {
        repositoryId: ensured.repositoryId,
        gitRef: ensured.gitRef,
        socketPath: attached.socketPath,
        nodeCount: ensured.nodeCount,
        corpusHash,
      };
      this.fileRoles = fileRolesFromParseCache(this.opts.root);
      this.refineAndPublishArchitecture();

      const subscription = await subscribeToSlots({
        socketPath: attached.socketPath,
        repositoryId: ensured.repositoryId,
        onChange: (change) => this.onDaemonSlotChanged(change),
        onDetach: () => {
          this.daemonOwnsFreshness = false;
          this.daemonSlot = null;
          this.slotSubscription = null;
        },
      });
      if (this.shuttingDown) {
        subscription.close();
        return true;
      }
      if (subscription.active) {
        this.slotSubscription = subscription;
        this.daemonOwnsFreshness = true;
      }
      this.conn.notify('window/logMessage', {
        type: 3,
        message: `Vibgrate graph: served by the local runtime (${ensured.repositoryId}, ${ensured.nodeCount} nodes) — this process holds no copy.`,
      });
      this.conn.notify('vibgrate/graph/status', { state: 'ready' } satisfies GraphStatusNotification);
      return true;
    } catch {
      return false;
    }
  }

  private onDaemonSlotChanged(change: {
    gitRef: string;
    nodeCount: number;
    corpusHash: string | null;
  }): void {
    if (this.daemonSlot) {
      this.daemonSlot.gitRef = change.gitRef;
      this.daemonSlot.nodeCount = change.nodeCount;
      this.daemonSlot.corpusHash = change.corpusHash;
    }
    this.fileRoles = fileRolesFromParseCache(this.opts.root);
    this.refineAndPublishArchitecture();
  }

  /** `--no-daemon` / no runtime: load or build the map in this process. */
  private async ensureGraphLocal(): Promise<void> {
    const existing = loadGraph(this.opts.root);
    if (existing) {
      this.graph = existing;
      this.fileRoles = fileRolesFromParseCache(this.opts.root);
      this.refineAndPublishArchitecture();
      this.conn.notify('vibgrate/graph/status', { state: 'ready' } satisfies GraphStatusNotification);
      void this.deferFreshnessToDaemon();
      return;
    }

    this.conn.notify('vibgrate/graph/status', { state: 'building' } satisfies GraphStatusNotification);
    const result = await buildGraph({ root: this.opts.root });
    writeArtifacts(result.graph, { root: this.opts.root, html: false, report: false });
    writeSnapshot(this.opts.root, result.graph.provenance.corpusHash, result.fileStats, {});
    this.graph = result.graph;
    this.fileRoles = result.fileRoles;
    this.refineAndPublishArchitecture();
    this.conn.notify('vibgrate/graph/status', { state: 'ready' } satisfies GraphStatusNotification);
    void this.deferFreshnessToDaemon();
  }

  /**
   * Hand freshness to the daemon, but only once it is actually holding this
   * map and pushing changes for it.
   *
   * The daemon watches a repository the moment it holds its slot — and not
   * before. Registration is not enough; this server has historically been one
   * of the roots vgd knew about but never had a map for. So the order matters:
   * publish the map we just loaded or built, take the repository id the daemon
   * answers with, and subscribe to *that* repository. Only a confirmed
   * subscription retires the local probe.
   *
   * Every failure mode lands on "keep watching for ourselves": no daemon, a
   * daemon that will not take the map, a subscription it does not confirm, or
   * a connection that later drops. None of them are errors — a machine with no
   * vgd is a supported configuration.
   */
  private async deferFreshnessToDaemon(): Promise<void> {
    if (this.opts.daemon === false || !this.opts.graph) return;
    if (this.daemonOwnsFreshness || this.slotSubscription || this.shuttingDown) return;
    try {
      const [{ publishGraphToVgd }, { subscribeToSlots }] = await Promise.all([
        import('../runtime/vgd/publish.js'),
        import('../runtime/vgd/slot-subscription.js'),
      ]);
      // Never starts a daemon — if none is running this returns and we keep
      // probing locally, exactly as before.
      const published = await publishGraphToVgd(this.opts.root);
      if (published.status !== 'published' && published.status !== 'current') return;
      const { repositoryId } = published;

      const subscription = await subscribeToSlots({
        repositoryId,
        onChange: () => this.reloadGraphFromDisk(),
        onDetach: () => {
          this.daemonOwnsFreshness = false;
          this.slotSubscription = null;
        },
      });
      if (!subscription.active) return;
      // `shutdown` can land while the socket was connecting; leaving the
      // subscription open would hold the process past exit.
      if (this.shuttingDown) {
        subscription.close();
        return;
      }
      this.slotSubscription = subscription;
      this.daemonOwnsFreshness = true;
      this.conn.notify('window/logMessage', {
        type: 3,
        message: `Vibgrate graph: freshness deferred to the local runtime (${repositoryId}).`,
      });
    } catch {
      /* No daemon, or it cannot push — this server keeps watching for itself. */
    }
  }

  /**
   * The graph for a query. The first call still builds the map (there is
   * nothing to answer from otherwise), but a *refresh* is only ever kicked off
   * here — never awaited to completion. The scheduler waits a micro-budget so a
   * warm probe can land, then we answer from the map we have; a rebuild that
   * outruns the budget is picked up by `onRefreshSettled` for the next query.
   */
  private async graphForQuery(): Promise<VgGraph | null> {
    await this.ensureGraph();
    if (this.daemonSlot) return null;
    if (!this.graph) return null;
    if (!this.daemonOwnsFreshness) await this.refresher.maybeRefresh();
    return this.graph;
  }

  /**
   * A refresh finished. Reload only when it actually rewrote the map — and
   * prefer the SQLite index, because re-parsing a six-figure-node `graph.json`
   * is itself multi-second work we must not do more often than the map changes.
   */
  private onRefreshSettled(outcome: RefreshOutcome | null): void {
    if (outcome?.status !== 'refreshed' || !outcome.wrote) return;
    this.reloadGraphFromDisk();
  }

  /**
   * Re-read the map we already have on disk. Shared by the local refresher and
   * by the daemon's `slot-changed` push, which are the only two things that
   * ever rewrite it — and never both at once.
   */
  private reloadGraphFromDisk(): void {
    const graphPath = resolveGraphPath(this.opts.root);
    const reloaded = loadGraphPreferIndex(this.opts.root, graphPath)?.graph ?? loadGraph(this.opts.root);
    if (!reloaded) return;
    this.graph = reloaded;
    this.fileRoles = fileRolesFromParseCache(this.opts.root);
    this.refineAndPublishArchitecture();
  }

  /**
   * Graph / AST-role refine can land after the first `publishScore()`. The
   * Architecture panel only re-pulls on a score or scope change, so a silent
   * in-memory refine would leave the first session on the path-only snapshot.
   * Re-push `vibgrate/score` (no-op when the artifact is not ready yet).
   */
  private refineArchitectureInPlace(): void {
    if (!this.artifact) return;
    if (this.graph) refineArtifactWithGraph(this.artifact, this.graph, this.fileRoles);
    else if (this.fileRoles.length > 0) refineArchitectureWithAstRoles(this.artifact, this.fileRoles);
  }

  private refineAndPublishArchitecture(): void {
    this.refineArchitectureInPlace();
    if (this.artifact) this.publishScore();
  }

  private async onGraphQuery(params: unknown): Promise<GraphQueryResult> {
    const p = params as GraphQueryParams;
    // The client renders a "Searching…" placeholder for the whole of this call,
    // so each stage is traced: a query that stalls should say where, not just
    // spin. Traces carry counts and timings only — never workspace text.
    const trace = (message: string): void =>
      this.conn.notify('window/logMessage', { type: 3, message: `Vibgrate graph: ${message}` });

    if (!this.opts.graph) {
      trace(`${p.mode} refused — the local Vibgrate Graph is turned off (--no-graph)`);
      return { ok: false, mode: p.mode, error: 'disabled', message: 'the local Vibgrate Graph is turned off' };
    }
    const started = Date.now();
    await this.ensureGraph();
    if (this.daemonSlot) {
      trace(`${p.mode} via vgd (${this.daemonSlot.nodeCount} nodes, ${this.daemonSlot.gitRef}) in ${Date.now() - started}ms`);
      return this.queryViaDaemon(p, trace);
    }
    const graph = await this.graphForQuery();
    if (!graph) {
      trace(`${p.mode} has no code map yet — it is still building`);
      return { ok: false, mode: p.mode, error: 'not-found', message: 'no code map yet — it is still building' };
    }
    trace(
      `${p.mode} over ${graph.nodes.length} nodes / ${graph.edges.length} edges ` +
        `(map ready in ${Date.now() - started}ms)`,
    );
    return runGraphQuery(graph, p, {
      root: this.opts.root,
      offline: this.opts.offline,
      semantic: this.opts.semantic,
      semanticSession: this.semanticSession,
      log: trace,
    });
  }

  private async queryViaDaemon(
    p: GraphQueryParams,
    trace: (message: string) => void,
  ): Promise<GraphQueryResult> {
    const slot = this.daemonSlot;
    if (!slot) {
      return { ok: false, mode: p.mode, error: 'not-found', message: 'no code map yet — it is still building' };
    }
    try {
      const { vgdRequest } = await import('../runtime/vgd/client.js');
      const res = await vgdRequest(
        {
          op: 'graph-query',
          repositoryId: slot.repositoryId,
          gitRef: slot.gitRef,
          mode: p.mode,
          question: p.question,
          semantic: p.semantic,
          budget: p.budget,
          limit: p.limit,
          name: p.name,
          depth: p.depth,
          a: p.a,
          b: p.b,
          callers: p.callers,
        },
        { socketPath: slot.socketPath },
      );
      if (!res.ok) {
        trace(`${p.mode} failed — ${res.error}`);
        return { ok: false, mode: p.mode, error: 'not-found', message: res.error };
      }
      if (!('result' in res) || !res.result || typeof res.result !== 'object') {
        return { ok: false, mode: p.mode, error: 'not-found', message: 'the local runtime returned no result' };
      }
      return res.result as GraphQueryResult;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      trace(`${p.mode} failed — ${message}`);
      return { ok: false, mode: p.mode, error: 'not-found', message };
    }
  }

  /**
   * The daemon's manifest digest for this workspace, or null when there is no
   * daemon (or it has not been told about this repo yet) — in which case the
   * caller computes it locally, exactly as before.
   */
  private async sharedManifestHash(): Promise<string | null> {
    try {
      const deps = await this.semanticSession.depContext();
      return deps?.manifestHash ?? null;
    } catch {
      return null;
    }
  }

  /**
   * `vibgrate/enrich` — lazy vulnerability enrichment for one expanded package,
   * straight from OSV.dev (never our API). Honours the server's offline flag.
   */
  private onEnrich(params: unknown): Promise<EnrichResult> {
    const p = (params ?? {}) as { ecosystem?: string | null; package?: string; version?: string | null };
    return enrichVulns(p.ecosystem ?? null, p.package ?? '', p.version ?? null, { offline: this.opts.offline });
  }

  /**
   * `vibgrate/vulns` — live OSV.dev pass over every resolved dependency in the
   * current workspace artifact. Powers the Vulnerabilities panel. Empty
   * findings with `source: 'osv'` means checked clean; `offline`/`error` mean
   * we could not check (absent ≠ zero). Attaches manifest locations, fixed-in
   * versions, CISA KEV flags, and a best-effort code-map reachability signal.
   */
  private async onVulns(): Promise<VulnsWorkspaceResult> {
    if (this.opts.offline) {
      return {
        findings: [],
        counts: { critical: 0, high: 0, moderate: 0, low: 0, unknown: 0 },
        packagesChecked: 0,
        source: 'offline',
        kevSource: 'skipped',
      };
    }
    const a = this.artifact;
    if (!a) {
      return {
        findings: [],
        counts: { critical: 0, high: 0, moderate: 0, low: 0, unknown: 0 },
        packagesChecked: 0,
        source: 'pending',
        kevSource: 'skipped',
      };
    }

    // Best-effort code-map import evidence (do not block on a cold build).
    const importMap = packageImportMap(this.graph);

    const targets: VulnTarget[] = [];
    for (const proj of a.projects ?? []) {
      // Stale LSP caches / older engines may still carry agent worktrees.
      // Never surface them in the Vulnerabilities panel.
      if (isPrunedWorkspacePath(proj.path) || isPrunedWorkspacePath(manifestRelativePath(proj))) {
        continue;
      }
      const ecosystem = osvEcosystem(proj.type);
      if (!ecosystem) continue;
      const relManifest = manifestRelativePath(proj);
      const absManifest = path.resolve(this.opts.root, relManifest);
      let text: string | null = null;
      try {
        if (fs.existsSync(absManifest)) text = fs.readFileSync(absManifest, 'utf8');
      } catch {
        text = null;
      }
      const kind = manifestKind(absManifest);
      for (const dep of proj.dependencies ?? []) {
        if (!dep.resolvedVersion) continue;
        let line: number | null = null;
        if (text && kind) {
          const l = findPackageLine(text, dep.package, kind);
          if (l >= 0) line = l + 1; // 1-based for the editor
        }
        let reachability: Reachability = 'unknown';
        let reachSites: VulnReachSite[] = [];
        let reachEvidence: string | null = null;
        if (importMap) {
          const files = lookupImportFiles(importMap, dep.package);
          if (files.length > 0) {
            // Package-level import evidence is "reached" for the IDE chip
            // (honest: import seen, not necessarily a vulnerable symbol).
            reachability = 'reached';
            reachSites = files.slice(0, 10).map((file) => ({
              file,
              // Best-effort first line that mentions the package name (import site).
              line: firstPackageMentionLine(this.opts.root, file, dep.package),
            }));
            reachEvidence = `${dep.package} is imported by ${files.length} file${files.length === 1 ? '' : 's'} in the code map`;
          } else {
            reachability = 'not-observed';
            reachEvidence = `no import of ${dep.package} was found in the code map`;
          }
        }
        targets.push({
          ecosystem,
          package: dep.package,
          version: dep.resolvedVersion,
          file: relManifest,
          line,
          section: dep.section ?? null,
          latestStable: dep.latestStable ?? null,
          reachability,
          reachSites,
          reachEvidence,
        });
      }
    }
    return scanWorkspaceVulns(targets, { offline: this.opts.offline });
  }

  private onScoreForFile(params: unknown): ScoreNotification | null {
    const p = params as { uri?: string };
    const a = this.artifact;
    if (!a || !p.uri) return null;
    const project = projectForFile(this.opts.root, a, uriToPath(p.uri));
    return project ? scoreForProject(this.opts.root, a, project) : null;
  }

  /**
   * `vibgrate/score/forProject` — monorepo scope picker. Returns the same
   * shape as the whole-repo score, scoped to one scanned project path.
   */
  private onScoreForProject(params: unknown): ScoreNotification | null {
    const p = params as { path?: string };
    const a = this.artifact;
    if (!a || typeof p.path !== 'string') return null;
    const project = projectByPath(a, p.path);
    return project ? scoreForProject(this.opts.root, a, project) : null;
  }

  /**
   * `vibgrate/architecture` — the Architecture panel. `path` absent or
   * `'__repo__'` returns the whole-workspace aggregate (`extended.architecture`);
   * a project path returns that project's own detection (`ProjectScan.architecture`).
   * Returns `null` when the scanner hasn't produced architecture data yet (still
   * scanning, or the archetype/layer scanner is disabled) rather than an empty
   * shape a client might mistake for "no layers detected".
   */
  private async onArchitecture(params: unknown): Promise<ArchitectureResponse | null> {
    const p = params as { path?: string } | undefined;
    const a = this.artifact;
    if (!a) return null;
    if (!p?.path || p.path === '__repo__') {
      const arch = a.extended?.architecture;
      if (!arch) return null;
      return {
        ...architectureWire(arch),
        ...this.architectureCallables('__repo__'),
        ...(await this.architectureMap('__repo__')),
        ...this.architectureExternals(),
      };
    }
    const project = projectByPath(a, p.path);
    if (!project?.architecture) {
      const arch = a.extended?.architecture;
      if (!arch) return null;
      return {
        ...architectureWire(arch),
        ...this.architectureCallables(p.path),
        ...(await this.architectureMap(p.path)),
        ...this.architectureExternals(),
      };
    }
    return {
      ...architectureWire(project.architecture),
      manifestPath: manifestRelativePath(project),
      lockfilePath: lockfileRelativePath(this.opts.root, project),
      ...this.architectureCallables(p.path),
      ...(await this.architectureMap(p.path)),
      ...this.architectureExternals(),
    };
  }

  /**
   * The Externals tab's payload: the tech stack and the external surface
   * inventory, exactly as the scan produced them. Both halves are optional and
   * independent — a scan can carry a tech stack with no inventory (no surface
   * catalog available) — so each is omitted rather than emptied.
   */
  private architectureExternals(): Pick<ArchitectureResponse, 'externals'> {
    const extended = this.artifact?.extended;
    if (!extended) return {};
    const techStack = techStackGroups(extended.toolingInventory);
    const surfaces = extended.surfaceInventory;
    if (!techStack && !surfaces) return {};
    return { externals: { ...(techStack ? { techStack } : {}), ...(surfaces ? { surfaces } : {}) } };
  }

  /**
   * Hierarchical map payloads for the Architecture board. Best-effort: a
   * missing graph omits the fields rather than failing the file-layer response.
   */
  private async architectureMap(scopePath: string): Promise<Pick<ArchitectureResponse, 'overview' | 'slice'>> {
    const graph = this.graphForArchitecture();
    if (!graph) return {};
    try {
      const sidecar = this.architectureSidecar(graph);
      const provider = await loadHaileProvider();
      const overview = overviewOf(graph, sidecar, provider, this.opts.root);
      if (!scopePath || scopePath === '__repo__') return { overview };
      const want = scopePath.replace(/\\/g, '/');
      const pkg = overview.packages.find(
        (p) => p.path === want || p.path.endsWith(`/${want}`) || p.id === want || p.name === want,
      );
      if (!pkg) return { overview };
      return {
        overview,
        slice: sliceOf(graph, sidecar, provider, { packageId: pkg.id, view: 'job', architecture: true, tests: false }),
      };
    } catch {
      return {};
    }
  }

  /**
   * `vibgrate/architecture/page` — HTML for the Architecture board (VS Code
   * webview or `vg show arch`). Overview is included so the host can paint L0
   * without a second round trip.
   */
  private async onArchitecturePage(params: unknown): Promise<{ html: string; overview: ArchOverview | null; layout: ArchBoardLayout } | null> {
    const p = params as { host?: string; nonce?: string } | undefined;
    const host = p?.host === 'vscode' ? 'vscode' : 'browser';
    const nonce = typeof p?.nonce === 'string' ? p.nonce : undefined;
    try {
      const provider = await loadHaileProvider();
      const html = architecturePageHtml(provider, { host, nonce });
      const graph = this.graphForArchitecture();
      const layout = readBoardLayout(this.opts.root) ?? defaultBoardLayout();
      if (!graph) return { html, overview: null, layout };
      const sidecar = this.architectureSidecar(graph);
      return { html, overview: overviewOf(graph, sidecar, provider, this.opts.root), layout };
    } catch {
      return null;
    }
  }

  /**
   * `vibgrate/architecture/slice` — one package's column map.
   */
  private async onArchitectureSlice(params: unknown): Promise<ArchSlice | null> {
    const p = params as {
      packageId?: string;
      view?: string;
      focus?: string;
      architecture?: boolean;
      tests?: boolean;
      cap?: number;
      expand?: boolean;
    };
    const graph = this.graphForArchitecture();
    if (!graph || typeof p?.packageId !== 'string' || !p.packageId) return null;
    try {
      const sidecar = this.architectureSidecar(graph);
      const provider = await loadHaileProvider();
      return sliceOf(
        graph,
        sidecar,
        provider,
        {
          packageId: p.packageId,
          view: parseArchView(p.view),
          focus: typeof p.focus === 'string' ? p.focus : undefined,
          architecture: p.architecture !== false,
          tests: p.tests === true,
          cap: typeof p.cap === 'number' && p.cap > 0 ? p.cap : undefined,
          expand: p.expand === true,
        },
        this.opts.root,
      );
    } catch {
      return null;
    }
  }

  /**
   * `vibgrate/architecture/ui` — minified map canvas (React Flow). Null when
   * the architecture module did not ship the pack; the host keeps vanilla HTML.
   */
  private async onArchitectureUi(): Promise<{ js: string | null }> {
    try {
      const provider = await loadHaileProvider();
      return { js: readArchUiScript(provider) };
    } catch {
      return { js: null };
    }
  }

  /**
   * `vibgrate/architecture/layout` — read or write `.vibgrate/board.arch.json`.
   */
  private onArchitectureLayout(params: unknown): ArchBoardLayout {
    const p = params as { layout?: unknown } | undefined;
    if (p?.layout && typeof p.layout === 'object') {
      const current = readBoardLayout(this.opts.root) ?? defaultBoardLayout();
      return writeBoardLayout(this.opts.root, {
        ...current,
        ...(p.layout as object),
        magic: BOARD_LAYOUT_MAGIC,
      } as ArchBoardLayout);
    }
    return readBoardLayout(this.opts.root) ?? defaultBoardLayout();
  }

  /**
   * `vibgrate/architecture/node` — inspector payload for a card (`vg show --json`).
   */
  private onArchitectureNode(params: unknown): unknown {
    const p = params as { id?: string };
    const graph = this.graphForArchitecture();
    if (!graph || typeof p?.id !== 'string' || !p.id) return null;
    const node = graph.nodes.find((n) => n.id === p.id || n.qualifiedName === p.id || n.name === p.id);
    if (!node) return null;
    try {
      return showJsonFor(graph, node, this.architectureSidecar(graph));
    } catch {
      return null;
    }
  }

  /** Disk graph is enough for the board even when the daemon owns the in-memory copy. */
  private graphForArchitecture(): VgGraph | null {
    return this.graph ?? loadGraph(this.opts.root);
  }

  private architectureSidecar(_graph: VgGraph) {
    const graphPath = resolveGraphPath(this.opts.root);
    return readHaileSidecar(graphPath);
  }

  /**
   * The architecture-module half of `vibgrate/architecture`: classified
   * callables for the scope plus the module's status. Additive and
   * best-effort — a missing or stale classify file simply omits `callables`,
   * and nothing here can fail the file-layer response.
   */
  private architectureCallables(scopePath: string): Pick<ArchitectureResponse, 'callables' | 'architectureModule'> {
    try {
      const corpusHash = this.daemonSlot?.corpusHash ?? this.graph?.provenance?.corpusHash;
      const status = haileModuleStatus();
      const summary = status.status === 'present' ? architectureSidecarSummary(this.opts.root, { corpusHash }) : undefined;
      const out: Pick<ArchitectureResponse, 'callables' | 'architectureModule'> = {
        architectureModule: { ...status, ...(summary ?? {}) },
      };
      const callables = loadArchitectureCallables(this.opts.root, scopePath, { corpusHash });
      if (callables) out.callables = callables;
      return out;
    } catch {
      return {};
    }
  }

  /**
   * `vibgrate/architecture/projects` — the workspace-scope Projects view.
   * One row per scanned project that produced architecture data, so a client
   * can render the whole portfolio without N round trips. Projects the
   * scanner never classified are omitted rather than shown as empty rows.
   */
  private onArchitectureProjects(): ArchitectureProjectSummary[] | null {
    const a = this.artifact;
    if (!a) return null;
    const rows: ArchitectureProjectSummary[] = [];
    for (const project of a.projects ?? []) {
      const arch = project.architecture;
      if (!arch) continue;
      const row: ArchitectureProjectSummary = {
        path: project.path,
        name: project.name,
        // `violations` absent means "no graph was available to check against",
        // which is not the same as "checked and clean" — keep them distinct.
        violations: arch.violations ? arch.violations.length : null,
        layerCount: arch.layers.filter((l) => l.fileCount > 0).length,
        fileCount: arch.totalClassified,
      };
      if (arch.projectKind) row.projectKind = arch.projectKind;
      if (arch.artifactKind) row.artifactKind = arch.artifactKind;
      if (arch.structure) row.structure = arch.structure.primary;
      if (arch.supportLevel !== undefined) row.supportLevel = arch.supportLevel;
      if (arch.packIds && arch.packIds.length > 0) row.packIds = arch.packIds;
      rows.push(row);
    }
    return rows;
  }
}

// ── Helpers ────────────────────────────────────────────────────────────────

function uriToPath(uri: string): string {
  try {
    return fileURLToPath(uri);
  } catch {
    return uri;
  }
}

/**
 * Run the same core scan as the CLI, but drop the `format: 'json'` console dump.
 *
 * `runCoreScan` always `console.log`s the prettified artifact when format is
 * json and `out` is unset. In the LSP process that lands on stderr (stdout is
 * reserved for the protocol) and used to flood Output ▸ Vibgrate. The return
 * value is unchanged; the full JSON is re-emitted on `vibgrate/scanArtifact`.
 *
 * Non-JSON console.log lines (timeouts, "no projects found", …) still pass
 * through so operational warnings remain visible on the main log channel.
 */
async function runCoreScanSilently(
  root: string,
  opts: ScanOptions,
  advanced: Awaited<ReturnType<typeof loadAdvancedScanHook>>,
  graph?: VgGraph | null,
  fileRoles?: AstRoleHit[],
): Promise<ScanArtifact> {
  const realLog = console.log.bind(console);
  console.log = (...args: unknown[]) => {
    if (args.length === 1 && typeof args[0] === 'string' && looksLikeScanArtifactJson(args[0])) {
      return;
    }
    realLog(...args);
  };
  try {
    const artifact = await runCoreScan(root, opts, advanced);
    if (graph) refineArtifactWithGraph(artifact, graph, fileRoles);
    return artifact;
  } finally {
    console.log = realLog;
  }
}

function architectureGraphView(graph: VgGraph) {
  return {
    nodes: graph.nodes.map((n) => ({ id: n.id, file: n.file, kind: n.kind })),
    edges: graph.edges.map((e) => ({
      src: e.src,
      dst: e.dst,
      kind: e.kind,
      confidence: e.confidence,
      resolution: e.resolution,
    })),
  };
}

function refineArtifactWithGraph(
  artifact: ScanArtifact,
  graph: VgGraph,
  fileRoles?: import('../core-open/scanners/architecture/ast-roles.js').AstRoleHit[],
): void {
  const hasArch = Boolean(artifact.extended?.architecture)
    || artifact.projects.some((p) => p.architecture);
  if (!hasArch) return;
  const already = artifact.extended?.architecture?.violations !== undefined
    || artifact.projects.some((p) => p.architecture?.violations !== undefined);
  if (!already) refineArchitectureWithGraph(artifact, architectureGraphView(graph));
  if (fileRoles && fileRoles.length > 0) {
    refineArchitectureWithAstRoles(artifact, fileRoles);
  }
}

/** Heuristic: the prettified scan artifact dump from `format: 'json'`. */
function looksLikeScanArtifactJson(text: string): boolean {
  const t = text.trimStart();
  if (!t.startsWith('{')) return false;
  // Artifact shape — projects inventory plus either drift score or timestamp.
  return (
    t.includes('"projects"') &&
    (t.includes('"drift"') || t.includes('"timestamp"') || t.includes('"dependencies"'))
  );
}

/**
 * True when a workspace-relative path sits under a tree the scanner must never
 * bill or score — hidden agent/tool dirs (`.claude/worktrees`, `.cursor`, …),
 * `SKIP_DIRS` (node_modules, dist, …), custom virtualenvs (`venv-*`), and
 * Python install trees (`site-packages`). Used to scrub stale LSP cache hits
 * and to keep the Vulnerabilities panel honest even when the artifact is old.
 */
function isPrunedWorkspacePath(relPath: string | null | undefined): boolean {
  if (!relPath || relPath === '.' || relPath === '') return false;
  const parts = relPath.split(/[/\\]/).filter(Boolean);
  return parts.some((p) => isSkippedDirName(p));
}

/** Drop pruned projects/findings from a scan artifact (cache scrub + safety net). */
function pruneArtifactProjects(a: ScanArtifact): ScanArtifact {
  const projects = (a.projects ?? []).filter(
    (p) => !isPrunedWorkspacePath(p.path) && !isPrunedWorkspacePath(manifestRelativePath(p)),
  );
  if (projects.length === (a.projects ?? []).length) {
    // Still scrub findings that point into pruned trees (e.g. only location is under .claude).
    const findings = (a.findings ?? []).filter((f) => !isPrunedWorkspacePath(f.location));
    if (findings.length === (a.findings ?? []).length) return a;
    return { ...a, findings };
  }
  const findings = (a.findings ?? []).filter((f) => !isPrunedWorkspacePath(f.location));
  return { ...a, projects, findings };
}

/** Find a scanned project by its workspace-relative path (scope picker). */
function projectByPath(a: ScanArtifact, projectPath: string): ProjectScan | undefined {
  // Clients send `/`-separated paths; artifacts from older Windows engines may
  // still carry `\`. Fold both to one form before comparing.
  const norm = (p: string): string => path.normalize((p === '' ? '.' : p).replace(/\\/g, '/'));
  const want = norm(projectPath);
  for (const proj of a.projects ?? []) {
    if (norm(proj.path) === want) return proj;
  }
  return undefined;
}

/**
 * Compact monorepo project list for the panel scope picker.
 *
 * Source of truth: scanned projects that carry a DriftScore (same set as
 * `.vibgrate/project_scores.json` / Explorer badges). Full workspace-relative
 * paths are preserved so packages with the same leaf name stay distinct.
 * Duplicate paths in the artifact are collapsed (prefer the scored entry).
 */
function buildProjectRefs(rootDir: string, projects: ProjectScan[]): ProjectRef[] | undefined {
  if (projects.length === 0) return undefined;

  const byPath = new Map<string, ProjectScan>();
  for (const p of projects) {
    // Wire paths are always `/`-separated. Scanners normalise at source now,
    // but cached artifacts written by older engines on Windows still mix
    // `src\foo` with `src/foo` — without this the picker neither dedupes nor
    // sorts them together.
    const rel = (p.path === '' ? '.' : p.path).replace(/\\/g, '/');
    const prev = byPath.get(rel);
    if (!prev) {
      byPath.set(rel, p);
      continue;
    }
    // Prefer the entry that carries a score when the scan lists a path twice.
    if (!prev.drift && p.drift) byPath.set(rel, p);
  }

  const refs: ProjectRef[] = [];
  for (const [rel, p] of byPath) {
    // Scope picker = monorepo packages the scan scored. Unscored discovery
    // noise (if any) stays out of the dropdown.
    if (!p.drift) continue;
    const lockfilePath = lockfileRelativePath(rootDir, p);
    refs.push({
      path: rel,
      // Full relative path for display/identity; package name alone collides
      // across nested fixtures (e.g. two "mcp" leaves).
      name: rel,
      manifestPath: manifestRelativePath(p),
      ...(lockfilePath ? { lockfilePath } : {}),
      score: p.drift.score,
      band: (p.drift.riskLevel ?? 'low') as Band,
      mode: (p.drift.mode ?? 'verified') as 'verified' | 'estimated',
    });
  }
  refs.sort((a, b) => a.path.localeCompare(b.path));
  return refs.length > 0 ? refs : undefined;
}

function projectForFile(root: string, a: ScanArtifact, filePath: string): ProjectScan | undefined {
  return matchProjectForFile(root, a.projects ?? [], filePath);
}

function sameProject(location: string, projectPath: string): boolean {
  return path.normalize(location) === path.normalize(projectPath);
}

/** Does the artifact carry release-date data? Drives Verified vs Estimated. */
function hasReleaseDates(a: ScanArtifact): boolean {
  for (const proj of a.projects ?? []) {
    for (const dep of proj.dependencies ?? []) {
      if (dep.ageDays !== null && dep.ageDays !== undefined) return true;
    }
  }
  return false;
}

/**
 * `vibgrate/score/forFile` payload for one project — same shape as the
 * whole-repo `ScoreNotification`, scoped to a single manifest. Reuses the
 * per-project `drift` the scan already computed (`ProjectScan.drift`) rather
 * than re-deriving a score client-side, for the same "one engine" reason the
 * whole-repo score is never recomputed in a client.
 */
function scoreForProject(rootDir: string, a: ScanArtifact, proj: ProjectScan): ScoreNotification | null {
  if (!proj.drift) return null;

  let behind = 0;
  let total = 0;
  for (const dep of proj.dependencies ?? []) {
    total++;
    if (dep.drift === 'major-behind' || dep.drift === 'minor-behind') behind++;
  }
  const projFindings = (a.findings ?? []).filter((f) => sameProject(f.location, proj.path));
  const eol = projFindings.filter((f) => f.ruleId === 'vibgrate/runtime-eol' || f.ruleId === 'vibgrate/eol').length;
  const unmaintained = projFindings.filter(
    (f) => f.ruleId === 'vibgrate/unmaintained' || f.ruleId === 'vibgrate/abandoned',
  ).length;
  const hasDates = (proj.dependencies ?? []).some((d) => d.ageDays !== null && d.ageDays !== undefined);

  return {
    score: proj.drift.score,
    band: (proj.drift.riskLevel ?? 'low') as Band,
    mode: proj.drift.mode ?? (hasDates ? 'verified' : 'estimated'),
    methodology: proj.drift.methodologyVersion ?? a.drift.methodologyVersion ?? 'unknown',
    scale: '0 best, 100 worst',
    counts: { behind, eol, unmaintained, total },
    rootPath: proj.path === '' ? '.' : proj.path,
    scannedAt: a.timestamp,
    ...(buildBreakdown(rootDir, [proj]) ? { breakdown: buildBreakdown(rootDir, [proj]) } : {}),
    ...(buildEolHorizon([proj]) ? { eolHorizon: buildEolHorizon([proj]) } : {}),
    ...(buildProjectRefs(rootDir, a.projects ?? [])
      ? { projects: buildProjectRefs(rootDir, a.projects ?? []) }
      : {}),
  };
}

/**
 * Runtime label for a project, e.g. "Node.js 20" — mirrors the label used in
 * the EOL/lag findings above, so the horizon reads consistently with them.
 */
function runtimeHorizonLabel(project: ProjectScan): string {
  const kind =
    project.type === 'node' ? 'Node.js' :
    project.type === 'dotnet' ? '.NET' :
    project.type === 'python' ? 'Python' :
    project.type === 'java' ? 'Java' : project.type;
  return project.runtime ? `${kind} ${project.runtime}` : kind;
}

/**
 * EOL horizon (plan §5.6): the runtimes with a known, real end-of-life date,
 * nearest first — whether that date is still ahead ("EOL in 42 days", advance
 * warning) or already behind (most-overdue reads first, same urgency order as
 * a negative-first sort gives naturally). Built only from
 * `ProjectScan.runtimeEolDate`, which the scanners populate from the Runtime
 * Catalog (endoflife.date) — never invented, never the major-lag proxy. One
 * entry per distinct runtime label (a monorepo with five Node 18 projects
 * shows one horizon line, not five).
 */
function buildEolHorizon(projects: ProjectScan[], now = Date.now()): EolHorizonItem[] | undefined {
  const byLabel = new Map<string, EolHorizonItem>();
  for (const p of projects) {
    if (!p.runtimeEolDate) continue;
    const t = Date.parse(p.runtimeEolDate);
    if (!Number.isFinite(t)) continue;
    const label = runtimeHorizonLabel(p);
    if (byLabel.has(label)) continue; // first occurrence wins; they share a date
    byLabel.set(label, {
      runtime: label,
      eolDate: p.runtimeEolDate,
      daysUntil: Math.round((t - now) / (24 * 60 * 60 * 1000)),
    });
  }
  if (byLabel.size === 0) return undefined;
  return [...byLabel.values()].sort((a, b) => a.daysUntil - b.daysUntil).slice(0, 5);
}

/** Band for a 0–100 drift number — the reconciled score bands (§6.5). The
 *  server sends the band; the client never re-derives one. */
function bandForScore(n: number): Band {
  if (n <= 30) return 'low';
  if (n <= 60) return 'moderate';
  return 'high';
}

/**
 * Full dependency inventory for the Dependencies panel. Scores every unique
 * package with the same `perDependencyDrift` scorer the DriftScore aggregate
 * uses (so row numbers match the portfolio score), then ranks worst-first.
 * Current packages stay in the list at drift 0 — Snyk-style inventory, not
 * only the offenders. Excluded placeholder stubs are omitted.
 *
 * Collects every project manifest that pins the package (with best-effort
 * declaration line) so the panel can open each hit — same pattern as vulns.
 */
function buildBreakdown(rootDir: string, projects: ProjectScan[]): BreakdownItem[] | undefined {
  type Acc = {
    row: DependencyRow;
    ecosystem: string | null;
    locations: BreakdownLocation[];
  };
  const byPkg = new Map<string, Acc>();

  for (const p of projects) {
    const relManifest = manifestRelativePath(p);
    if (isPrunedWorkspacePath(p.path) || isPrunedWorkspacePath(relManifest)) continue;
    const ecosystem = osvEcosystem(p.type);

    // Read each manifest once; line lookup is best-effort (absent when missing).
    const absManifest = path.resolve(rootDir, relManifest);
    let text: string | null = null;
    try {
      if (fs.existsSync(absManifest)) text = fs.readFileSync(absManifest, 'utf8');
    } catch {
      text = null;
    }
    const kind = manifestKind(absManifest);

    for (const d of p.dependencies ?? []) {
      let line: number | null = null;
      if (text && kind !== 'unknown') {
        const l = findPackageLine(text, d.package, kind);
        if (l >= 0) line = l + 1; // 1-based for the editor
      }
      const loc: BreakdownLocation = { file: relManifest, line };
      const existing = byPkg.get(d.package);
      if (!existing) {
        byPkg.set(d.package, { row: d, ecosystem, locations: [loc] });
        continue;
      }
      if (!existing.locations.some((x) => x.file === loc.file && x.line === loc.line)) {
        existing.locations.push(loc);
      }
      // Prefer the worse drift row when the same package appears multiple times
      // (first-wins was silent; keep the higher-signal version for display).
      const prev = perDependencyDrift(existing.row, {
        unsupported: existing.row.unsupported === true,
        abandoned: existing.row.abandoned === true,
      });
      const next = perDependencyDrift(d, {
        unsupported: d.unsupported === true,
        abandoned: d.abandoned === true,
      });
      if (!next.excluded && (prev.excluded || next.drift > prev.drift)) {
        existing.row = d;
        if (ecosystem) existing.ecosystem = ecosystem;
      }
    }
  }
  if (byPkg.size === 0) return undefined;

  const items: BreakdownItem[] = [];
  for (const [, { row, ecosystem, locations }] of byPkg) {
    const scored = perDependencyDrift(row, {
      unsupported: row.unsupported === true,
      abandoned: row.abandoned === true,
    });
    if (scored.excluded) continue;
    items.push({
      package: row.package,
      section: row.section ?? 'dependencies',
      ecosystem,
      drift: scored.drift,
      band: bandForScore(scored.drift),
      currentSpec: row.currentSpec ?? '',
      resolvedVersion: row.resolvedVersion ?? null,
      latestStable: row.latestStable ?? null,
      majorsBehind: row.majorsBehind ?? null,
      ageDays: row.ageDays ?? null,
      mode: scored.mode,
      unsupported: scored.unsupported || row.unsupported === true,
      abandoned: scored.flags.includes('abandoned-floor') || row.abandoned === true,
      flags: scored.flags,
      locations,
    });
  }

  items.sort((a, b) => b.drift - a.drift || a.package.localeCompare(b.package));
  return items.length > 0 ? items : undefined;
}

/** Workspace-relative manifest path for a scanned project (for jump-to-line). */
function manifestRelativePath(proj: ProjectScan): string {
  const base = proj.path === '.' || proj.path === '' ? '' : proj.path;
  const join = (name: string) => (base ? path.join(base, name) : name);
  switch (proj.type) {
    case 'node':
    case 'typescript':
      return join('package.json');
    case 'python':
      return join('pyproject.toml');
    case 'go':
      return join('go.mod');
    case 'rust':
      return join('Cargo.toml');
    case 'ruby':
      return join('Gemfile');
    case 'php':
      return join('composer.json');
    case 'java':
    case 'kotlin':
    case 'scala':
    case 'groovy':
      return join('pom.xml');
    case 'dotnet':
      return join(proj.name ? `${proj.name}.csproj` : 'Directory.Build.props');
    case 'dart':
      return join('pubspec.yaml');
    case 'elixir':
      return join('mix.exs');
    default:
      return join('package.json');
  }
}

/**
 * Workspace-relative lockfile path for a scanned project, when one is present
 * on disk. Checked against the real filesystem (not just inferred from
 * ecosystem) since the lockfile may live at the project's own directory or,
 * for a JS/TS package inside a monorepo, at the workspace root instead.
 * Returns `undefined` when no known lockfile name exists for the project.
 */
function lockfileRelativePath(rootDir: string, proj: ProjectScan): string | undefined {
  const base = proj.path === '.' || proj.path === '' ? '' : proj.path;
  const candidateNames: string[] = (() => {
    switch (proj.type) {
      case 'node':
      case 'typescript':
        return ['pnpm-lock.yaml', 'yarn.lock', 'package-lock.json', 'bun.lockb'];
      case 'python':
        return ['poetry.lock', 'uv.lock', 'Pipfile.lock'];
      case 'go':
        return ['go.sum'];
      case 'rust':
        return ['Cargo.lock'];
      case 'ruby':
        return ['Gemfile.lock'];
      case 'php':
        return ['composer.lock'];
      case 'dotnet':
        return ['packages.lock.json'];
      case 'dart':
        return ['pubspec.lock'];
      default:
        return [];
    }
  })();
  const dirsToTry = base ? [base, ''] : [''];
  for (const dir of dirsToTry) {
    for (const name of candidateNames) {
      const rel = dir ? path.join(dir, name) : name;
      try {
        if (fs.existsSync(path.join(rootDir, rel))) return rel;
      } catch {
        // Unreadable path — try the next candidate.
      }
    }
  }
  return undefined;
}

/**
 * Map package name (lowered) → repo-relative files that import it.
 * Returns null when no graph is loaded — callers then report reachability unknown.
 */
function packageImportMap(graph: VgGraph | null | undefined): Map<string, string[]> | null {
  if (!graph?.nodes?.length) return null;
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  const map = new Map<string, Set<string>>();

  const addPkg = (raw: string, file: string) => {
    if (!file) return;
    const cleaned = raw.replace(/^node_modules\//, '').replace(/\\/g, '/');
    const keys = new Set<string>();
    keys.add(cleaned.toLowerCase());
    if (cleaned.startsWith('@')) {
      const parts = cleaned.split('/');
      if (parts.length >= 2) keys.add(`${parts[0]}/${parts[1]}`.toLowerCase());
    } else {
      keys.add(cleaned.split('/')[0]!.toLowerCase());
    }
    for (const k of keys) {
      let set = map.get(k);
      if (!set) {
        set = new Set();
        map.set(k, set);
      }
      set.add(file);
    }
  };

  // Prefer import edges (source file → external package).
  for (const e of graph.edges ?? []) {
    if (e.kind !== 'import') continue;
    const src = byId.get(e.src);
    const dst = byId.get(e.dst);
    if (!src?.file || !dst) continue;
    if (dst.kind !== 'external' && dst.kind !== 'package' && dst.kind !== 'module') continue;
    for (const raw of [dst.name, dst.qualifiedName].filter(Boolean) as string[]) {
      addPkg(raw, src.file);
    }
  }

  // Also index external nodes that somehow lack edges (defensive).
  for (const n of graph.nodes) {
    if (n.kind !== 'external' && n.kind !== 'package' && n.kind !== 'module') continue;
    // No file list without import edges — keys only for membership.
    for (const raw of [n.name, n.qualifiedName].filter(Boolean) as string[]) {
      const cleaned = raw.replace(/^node_modules\//, '').replace(/\\/g, '/').toLowerCase();
      if (!map.has(cleaned)) map.set(cleaned, new Set());
      if (cleaned.startsWith('@')) {
        const parts = cleaned.split('/');
        if (parts.length >= 2 && !map.has(`${parts[0]}/${parts[1]}`)) {
          map.set(`${parts[0]}/${parts[1]}`, new Set());
        }
      }
    }
  }

  const out = new Map<string, string[]>();
  for (const [k, files] of map) {
    out.set(k, [...files].sort());
  }
  return out;
}

function lookupImportFiles(map: Map<string, string[]>, pkg: string): string[] {
  const p = pkg.toLowerCase();
  if (map.has(p)) return map.get(p)!;
  if (!p.startsWith('@')) {
    const root = p.split('/')[0]!;
    if (map.has(root)) return map.get(root)!;
  }
  return [];
}

/**
 * First 1-based line in `relFile` that mentions the package (import site).
 * Best-effort and bounded; returns null when unreadable or no hit.
 */
function firstPackageMentionLine(root: string, relFile: string, pkg: string): number | null {
  try {
    const abs = path.resolve(root, relFile);
    const stat = fs.statSync(abs);
    if (!stat.isFile() || stat.size > 256 * 1024) return null;
    const text = fs.readFileSync(abs, 'utf8');
    // Prefer a token that appears in import lines: package name or last segment.
    const tokens = [pkg];
    if (pkg.startsWith('@')) {
      const parts = pkg.split('/');
      if (parts[1]) tokens.push(parts[1]);
    } else if (pkg.includes('/')) {
      tokens.push(pkg.split('/')[0]!);
    }
    const lines = text.split(/\r?\n/);
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]!;
      if (!/import|require|from|use |include/.test(line) && !line.includes(pkg)) continue;
      for (const t of tokens) {
        if (t && line.includes(t)) return i + 1;
      }
    }
  } catch {
    /* unreadable */
  }
  return null;
}

/** Map a scanned project type to its OSV.dev ecosystem slug (null = no OSV
 *  coverage, so the client skips vulnerability enrichment). */
function osvEcosystem(type: ProjectScan['type']): string | null {
  switch (type) {
    case 'node':
    case 'typescript': return 'npm';
    case 'python': return 'PyPI';
    case 'java':
    case 'kotlin':
    case 'scala':
    case 'groovy': return 'Maven';
    case 'go': return 'Go';
    case 'rust': return 'crates.io';
    case 'ruby': return 'RubyGems';
    case 'php': return 'Packagist';
    case 'dotnet': return 'NuGet';
    case 'dart': return 'Pub';
    case 'swift': return 'SwiftURL';
    case 'elixir': return 'Hex';
    default: return null;
  }
}

/**
 * The newest in-range update still open to this dependency — `latestSatisfying`
 * when it is a real step up from the resolved version, else null. Sized as
 * `minor`/`patch` so the label can name the step honestly.
 */
function inRangeStep(dep: DependencyRow): { version: string; size: 'minor' | 'patch' } | null {
  const target = dep.latestSatisfying;
  if (!target || !dep.resolvedVersion) return null;
  if (!semver.valid(target) || !semver.valid(dep.resolvedVersion)) return null;
  if (!semver.gt(target, dep.resolvedVersion)) return null;
  const diff = semver.diff(dep.resolvedVersion, target);
  return { version: target, size: diff === 'patch' || diff === 'prepatch' ? 'patch' : 'minor' };
}

/** The end-of-line decoration text. Terse: it sits in the user's code. */
function inlineLabel(dep: DependencyRow, state?: { ignored?: boolean; isNew?: boolean }): string {
  const bits: string[] = [];
  if (state?.ignored) bits.push('ignored');
  // Maintenance floors are score-defining facts — they belong on the line,
  // not only in the hover (coverage plan §6 P1-4, state vocabulary).
  if (dep.unsupported) bits.push('unsupported');
  else if (dep.abandoned) bits.push('abandoned');
  if (dep.drift === 'major-behind') {
    // A range anchored below the latest major can still have room inside it:
    // name the in-range target first (what an install alone can reach), then
    // the major journey — never a "minor behind" label next to a new-major
    // version number.
    const step = inRangeStep(dep);
    if (step) bits.push(`${step.size} behind`, step.version);
    const n = dep.majorsBehind ?? 1;
    bits.push(`${n} major behind`);
  } else if (dep.drift === 'minor-behind') {
    bits.push('minor behind');
  }
  if (dep.latestStable) bits.push(dep.latestStable);
  // ageDays is fractional off the wire — round it. "1,896.836d stale" in the
  // margin of someone's package.json is the kind of detail that reads as sloppy.
  if (dep.ageDays != null && dep.ageDays >= 1) {
    bits.push(`${Math.round(dep.ageDays).toLocaleString('en-US')}d stale`);
  }
  if (state?.isNew) bits.push('new');
  return bits.join(' · ');
}

/**
 * Hover for the runtime declaration line (engines/tool-versions/FROM…): the
 * EOL card. Only real facts: `ProjectScan.runtimeEolDate` (Runtime Catalog /
 * endoflife.date — never estimated) plus this project's runtime findings.
 * Returns null when there is nothing real to say.
 */
function runtimeHoverMarkdown(project: ProjectScan, findings: Finding[], now = Date.now()): string | null {
  const lines: string[] = [];
  const label = runtimeHorizonLabel(project);

  if (project.runtimeEolDate) {
    const t = Date.parse(project.runtimeEolDate);
    if (Number.isFinite(t)) {
      const days = Math.round((t - now) / (24 * 60 * 60 * 1000));
      const when =
        days < 0
          ? `**EOL ${project.runtimeEolDate}** — ${Math.abs(days).toLocaleString('en-US')} days ago`
          : `EOL ${project.runtimeEolDate} — in ${days.toLocaleString('en-US')} days`;
      lines.push(`**${label}**`);
      lines.push('');
      lines.push(days < 0 ? `$(warning) ${when}` : when);
    }
  }

  const runtimeFindings = findings
    .filter(
      (f) =>
        DIAGNOSTIC_RULES.has(f.ruleId) &&
        (f.ruleId.startsWith('vibgrate/runtime') || f.ruleId === 'vibgrate/eol'),
    )
    .filter((f) => sameProject(f.location, project.path));
  if (runtimeFindings.length) {
    if (!lines.length) {
      lines.push(`**${label}**`);
    }
    lines.push('');
    for (const f of runtimeFindings.slice(0, 4)) lines.push(`- ${f.message}`);
  }

  if (!lines.length) return null;
  lines.push('');
  lines.push('---');
  lines.push('$(shield) EOL dates from the Runtime Catalog (endoflife.date). Resolved locally.');
  return lines.join('\n');
}

/** `command:` URI for a `MarkdownString` link — one JSON-encoded arg object, matching `executeCommand`. */
function commandUri(command: string, args: unknown): string {
  return `command:${command}?${encodeURIComponent(JSON.stringify([args]))}`;
}

/** "2 majors behind" / "minor behind" — the hover card's badge text. Mirrors `inlineLabel`'s drift wording. */
function driftBadge(dep: DependencyRow): string | null {
  if (dep.drift === 'major-behind') {
    const n = dep.majorsBehind ?? 1;
    return `${n} major${n === 1 ? '' : 's'} behind`;
  }
  if (dep.drift === 'minor-behind') return 'minor behind';
  return null;
}

/**
 * "17.x → 18.x → 19.x" — a plain-text stand-in for the wireframe's version
 * ladder graphic (hover markdown can't draw one). Built only from the real
 * major-version numbers in `yours`/`latest`; long jumps are truncated with
 * `…` rather than listing every major, so a package that is a decade behind
 * doesn't produce a wall of numbers. Returns null when either version isn't
 * a parseable `<major>.x` (so nothing is shown rather than something wrong).
 */
function versionJourney(yours: string, latest: string): string | null {
  const from = Number(/^(\d+)/.exec(yours)?.[1]);
  const to = Number(/^(\d+)/.exec(latest)?.[1]);
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null;

  const majors: number[] = [];
  for (let m = from; m <= to; m++) majors.push(m);
  const shown = majors.length > 5 ? [majors[0], majors[1], -1, majors[majors.length - 2], majors[majors.length - 1]] : majors;
  return shown.map((m) => (m === -1 ? '…' : `${m}.x`)).join(' → ');
}

/**
 * The hover card (docs/ide-integration/wireframes/02-hover-card.svg). Shows
 * the facts we actually have: version currency, this dependency's real
 * contribution to the score, and real maintenance flags (`unsupported`/
 * `abandoned`).
 *
 * The wireframe also lists upstream release cadence, a last-commit date, a
 * breaking-change count, and transitive blast radius. None of those are
 * backed by real data today — no release-history or changelog source is
 * scanned, and the graph does not track per-npm-package import edges — so
 * they are left out rather than approximated. Fabricating them would violate
 * the same rule that keeps marketing visuals honest (no invented product
 * output); a hover card is not exempt.
 *
 * Two links, both real, not aspirational: "Fix →" opens the
 * existing ranked, vendor-neutral remediation menu (`routeFix.ts`) — per the
 * wireframe's own design notes, that is explicitly not a "Fix" button, so it
 * does not conflict with "no fix affordance" (plan §0.1). "Explain this
 * drift" focuses the Dependencies panel and expands this package's row,
 * which already carries the full explanation (guards, floors, age).
 */
function hoverMarkdown(
  dep: DependencyRow,
  contribution: number | null,
  state?: { ignored?: IgnoreEntry; isNew?: boolean },
): string {
  const lines: string[] = [];
  const badge = driftBadge(dep);
  const contributionText = contribution != null ? ` · contributes +${Math.round(contribution)} to drift` : '';
  lines.push(badge ? `**${dep.package}**  \`${badge}\`${contributionText}` : `**${dep.package}**`);
  lines.push('');
  if (state?.ignored) {
    const ig = state.ignored;
    const why = ig.reason ? ` — ${ig.reason}` : '';
    const until = ig.until ? ` (until ${ig.until})` : '';
    lines.push(`$(eye-closed) **Ignored**${why}${until} · \`.vibgrate/ignore.json\``);
    lines.push('');
  }
  if (state?.isNew) {
    lines.push('_New drift — this dependency was not behind in the previous recorded scan._');
    lines.push('');
  }

  const yours = dep.resolvedVersion ?? dep.currentSpec;
  const drifted = dep.drift === 'major-behind' || dep.drift === 'minor-behind';

  if (dep.latestStable && dep.latestStable !== yours) {
    lines.push('**Currency**');
    lines.push(`| | |`);
    lines.push(`|---|---|`);
    lines.push(`| yours | \`${yours}\` |`);
    const step = inRangeStep(dep);
    if (step && step.version !== dep.latestStable) {
      lines.push(`| latest in range | \`${step.version}\` |`);
    }
    lines.push(`| latest | \`${dep.latestStable}\` |`);
    const journey = versionJourney(yours, dep.latestStable);
    if (journey) lines.push(`| journey | ${journey} |`);
    if (dep.majorsBehind) lines.push(`| majors behind | ${dep.majorsBehind} |`);
    if (dep.ageDays != null && dep.ageDays >= 1) {
      lines.push(`| age | ${Math.round(dep.ageDays).toLocaleString('en-US')} days |`);
    }
    if (dep.libyears != null && dep.libyears >= 0.01) {
      lines.push(`| libyears | ${dep.libyears.toFixed(2)} |`);
    }
    const license = dep.license?.spdxId ?? dep.license?.raw;
    if (license) lines.push(`| license | ${license} |`);
  } else {
    lines.push(`\`${yours}\` — current.`);
  }

  if (dep.unsupported || dep.abandoned) {
    lines.push('');
    lines.push('**Maintenance**');
    if (dep.unsupported) lines.push('$(warning) Unsupported / deprecated — floored at 70.');
    if (dep.abandoned) lines.push('$(warning) Abandoned — no recent releases — floored at 50.');
  }

  if (drifted) {
    lines.push('');
    lines.push('---');
    const routeFixUri = commandUri('vibgrate.routeFix', {
      package: dep.package,
      from: yours,
      to: dep.latestStable ?? yours,
      majorsBehind: dep.majorsBehind ?? 0,
    });
    const explainUri = commandUri('vibgrate.explain', { package: dep.package });
    lines.push(`[Fix →](${routeFixUri}) · [Explain this drift](${explainUri})`);
  }

  lines.push('');
  lines.push('---');
  lines.push('$(shield) Resolved locally from your lockfile. No source uploaded.');
  return lines.join('\n');
}

// ── Entry point ────────────────────────────────────────────────────────────

export function startLanguageServer(opts: ServerOptions): void {
  if (!fs.existsSync(opts.root)) {
    process.stderr.write(`vg lsp: root does not exist: ${opts.root}\n`);
    process.exit(1);
  }

  /*
   * stdout is the protocol channel, and nothing else may touch it.
   *
   * A single stray `console.log` from anywhere in the scanner tree — a debug
   * line, a deprecation warning, a progress spinner — lands in the middle of a
   * framed message, desynchronises the reader, and the editor reports the
   * server as crashed. This is the most common way an LSP integration fails,
   * and it fails intermittently, which is worse.
   *
   * So we take the real stdout writer for ourselves, hand it to the protocol,
   * and then redirect `process.stdout` to stderr for the rest of the process.
   * After this point it is not possible for anything else to write to the
   * protocol stream, however deep in the call tree it lives.
   */
  const realWrite = process.stdout.write.bind(process.stdout);

  const protocolOut = {
    write(chunk: string | Uint8Array): boolean {
      return realWrite(chunk as never);
    },
  } as NodeJS.WritableStream;

  process.stdout.write = ((chunk: never, ...rest: never[]) =>
    process.stderr.write(chunk, ...rest)) as typeof process.stdout.write;

  process.stderr.write(
    `vg lsp ${VERSION} — serving ${pathToFileURL(opts.root).href}` +
      `${opts.offline ? ' (offline)' : ''}\n`,
  );

  new VibgrateLanguageServer(opts, protocolOut);
}
